import { useState, useEffect, useRef } from 'react';
import { AiOutlinePlus, AiOutlineHome, AiOutlineEye, AiOutlineCheck, AiOutlineClose, AiOutlineEdit, AiOutlineDelete, AiOutlineLink, AiOutlineEnvironment, AiOutlineDownload, AiOutlineCamera, AiOutlineUpload, AiOutlineSync, AiOutlineLoading3Quarters, AiOutlineSetting, AiOutlineBell, AiOutlineInfoCircle } from 'react-icons/ai';
import { BsSun, BsMoon, BsMap } from 'react-icons/bs';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { db } from './config/firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { analytics } from './utils/analytics';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons by view quality
const createMarkerIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Status options
const STATUSES = ['New', 'Watch', 'Tour', 'Pass', 'Offer'];
const STATUS_COLORS = {
  New: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  Watch: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Tour: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  Pass: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  Offer: 'bg-green-500/20 text-green-400 border-green-500/30',
};

// View quality options
const VIEW_RATINGS = ['None', 'Yard Only', 'Partial', 'Good', 'Excellent'];
const ELEVATION_POSITIONS = ['Low', 'Mid', 'High', 'Ridge'];
const NEIGHBORHOODS = ['Pulpit Rock', 'Northwest', 'West-Central', 'Other'];

// Neighborhood information data
const NEIGHBORHOOD_INFO = {
  'Pulpit Rock': {
    elevation: '6,400-6,621 ft',
    viewQuality: 'Excellent',
    schools: {
      district: 'Academy District 20',
      elementary: 'Pioneer Elementary (9/10)',
      middle: 'Mountain Ridge Middle (A-)',
      high: 'Rampart High (A-)'
    },
    safety: 'Top 10 safest neighborhood in Colorado Springs. Northeast area has best safety stats (1 in 50 vs 1 in 15 city-wide).',
    hoa: 'Mixed - some subdivisions have HOAs ($10-300/mo typical), others don\'t. Townhomes usually require HOA.',
    lifestyle: 'University Village nearby (Costco, Trader Joe\'s, Sprouts). Easy I-25 access. 500-acre Pulpit Rock Park with 5+ mile trails.',
    realEstate: 'Median ~$459K. Range $400K-$900K+. Premium for view properties near the rock ($1M+). Mostly 1960s-70s homes.',
    pros: ['Excellent Pikes Peak views', 'Top-rated schools', 'Safe neighborhood', 'Great hiking access', 'Convenient shopping'],
    cons: ['Older housing stock', 'Premium prices for views', 'Some wildfire risk'],
    bestFor: 'Families wanting excellent schools, outdoor enthusiasts, buyers prioritizing mountain views'
  }
};

// Calculate monthly payment
function calculateMonthly(price, downPaymentPercent = 20, rate = 6.5, propertyTaxRate = 0.5, insurance = 150, hoa = 0) {
  const downPayment = price * (downPaymentPercent / 100);
  const principal = price - downPayment;
  const monthlyRate = rate / 100 / 12;
  const numPayments = 30 * 12;

  const mortgage = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
  const propertyTax = (price * (propertyTaxRate / 100)) / 12;

  return Math.round(mortgage + propertyTax + insurance + hoa);
}

// Listing status options (from real estate sites)
const LISTING_STATUSES = ['Active', 'Pending', 'Sold', 'Unknown'];
const LISTING_STATUS_COLORS = {
  Active: 'bg-green-500/20 text-green-400 border-green-500/30',
  Pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Sold: 'bg-red-500/20 text-red-400 border-red-500/30',
  Unknown: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

// Empty listing template
const emptyListing = {
  address: '',
  price: '',
  beds: '',
  baths: '',
  sqft: '',
  lotSize: '',
  hoa: '',
  viewClaim: '',
  viewReality: 'None',
  elevation: 'Mid',
  orientation: 'W',
  neighborhood: 'Northwest',
  noiseLevel: '', // quiet, moderate, busy, loud
  status: 'New',
  listingStatus: 'Active', // Active, Pending, Sold
  originalPrice: '', // Track original price for price drops
  daysOnMarket: '', // Days listed
  notes: '',
  link: '',
  photo: '', // Base64 image or URL
  dateAdded: new Date().toISOString().split('T')[0],
};

// Export to CSV
function exportToCSV(listings) {
  const headers = ['Address', 'Price', 'Beds', 'Baths', 'SqFt', 'Lot Size', 'HOA', 'View Claim', 'View Reality', 'Elevation', 'Orientation', 'Neighborhood', 'Status', 'Notes', 'Link', 'Date Added', 'Est Monthly'];

  const rows = listings.map(l => [
    l.address,
    l.price,
    l.beds,
    l.baths,
    l.sqft,
    l.lotSize,
    l.hoa,
    l.viewClaim,
    l.viewReality,
    l.elevation,
    l.orientation,
    l.neighborhood,
    l.status,
    l.notes?.replace(/,/g, ';'),
    l.link,
    l.dateAdded,
    calculateMonthly(parseInt(l.price) || 0, 20, 6.5, 0.5, 150, parseInt(l.hoa) || 0)
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(r => r.map(cell => `"${cell || ''}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `house-hunter-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Firestore collection name
const LISTINGS_COLLECTION = 'house-hunter-listings';

// Pikes Peak coordinates (for calculating orientation)
const PIKES_PEAK = { lat: 38.8409, lng: -105.0423 };

// Geocode address using Nominatim (free, no API key)
async function geocodeAddress(address) {
  try {
    const query = encodeURIComponent(`${address}, Colorado Springs, CO`);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1&addressdetails=1`,
      { headers: { 'User-Agent': 'HouseHunter/1.0' } }
    );
    const data = await response.json();
    if (data && data[0]) {
      const addr = data[0].address || {};
      // Extract neighborhood from various Nominatim fields
      const neighborhood = addr.neighbourhood || addr.suburb || addr.quarter ||
                          addr.residential || addr.city_district || null;
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        neighborhood: neighborhood,
        displayName: data[0].display_name
      };
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
  return null;
}

// Map Nominatim neighborhood names to our dropdown options
function mapNeighborhood(nominatimNeighborhood) {
  if (!nominatimNeighborhood) return null;
  const name = nominatimNeighborhood.toLowerCase();

  if (name.includes('pulpit rock')) return 'Pulpit Rock';
  if (name.includes('northwest') || name.includes('rockrimmon') ||
      name.includes('peregrine') || name.includes('flying horse')) return 'Northwest';
  if (name.includes('west') || name.includes('skyway') ||
      name.includes('cheyenne') || name.includes('broadmoor')) return 'West-Central';

  // Return 'Other' for neighborhoods we don't specifically track
  return 'Other';
}

// Get elevation from Open-Elevation API
async function getElevation(lat, lng) {
  try {
    const response = await fetch(
      `https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lng}`
    );
    const data = await response.json();
    if (data && data.results && data.results[0]) {
      return data.results[0].elevation; // meters
    }
  } catch (error) {
    console.error('Elevation API error:', error);
  }
  return null;
}

// Convert elevation in meters to category
// Colorado Springs base: ~1,800m (6,000 ft)
// Foothills: 1,900-2,100m (6,200-6,900 ft)
// Higher: 2,100m+ (6,900+ ft)
function elevationToCategory(meters) {
  if (meters === null) return 'Mid';
  const feet = meters * 3.28084;
  if (feet < 6200) return 'Low';
  if (feet < 6600) return 'Mid';
  if (feet < 7000) return 'High';
  return 'Ridge';
}

// Calculate orientation relative to Pikes Peak/mountains (west)
function calculateOrientation(lat, lng) {
  // Calculate bearing from property to Pikes Peak
  const dLng = (PIKES_PEAK.lng - lng) * Math.PI / 180;
  const lat1 = lat * Math.PI / 180;
  const lat2 = PIKES_PEAK.lat * Math.PI / 180;

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  bearing = (bearing + 360) % 360;

  // Convert bearing to compass direction (this is direction TO mountains)
  // For view orientation, we want which way the house should face to see mountains
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(bearing / 45) % 8;
  return directions[index];
}

function App() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [currentListing, setCurrentListing] = useState({ ...emptyListing });
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortBy, setSortBy] = useState('dateAdded');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupStep, setLookupStep] = useState(''); // 'geocoding', 'elevation', 'satellite'
  const [toast, setToast] = useState(null);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [listingUrl, setListingUrl] = useState('');
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [photoAnalysis, setPhotoAnalysis] = useState(null);
  const [showSatelliteModal, setShowSatelliteModal] = useState(false);
  const [satelliteData, setSatelliteData] = useState(null);
  const [tourStep, setTourStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [weather, setWeather] = useState(null);
  const [highlightAiAnalysis, setHighlightAiAnalysis] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('house-hunter-dark-mode');
    return saved !== null ? JSON.parse(saved) : true; // Default to dark
  });
  const [showMapView, setShowMapView] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [alertCriteria, setAlertCriteria] = useState({
    location: 'Colorado Springs, CO',
    minPrice: 350000,
    maxPrice: 550000,
    minBeds: 3,
    maxBeds: 5,
    minBaths: 2,
    minSqft: 1500,
    maxSqft: 3500,
    requireMountainView: true,
    preferredOrientations: ['W', 'SW', 'NW'],
    maxNoiseLevel: 'moderate',
    minElevation: 6000,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [showNeighborhoodModal, setShowNeighborhoodModal] = useState(false);
  const [neighborhoodContent, setNeighborhoodContent] = useState(null);
  const [showTestResultsModal, setShowTestResultsModal] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [runningTest, setRunningTest] = useState(false);
  const [activeTab, setActiveTab] = useState('listings'); // 'listings' or 'inbox'
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Lock body scroll when any modal is open (prevents background scroll on mobile)
  useEffect(() => {
    const isAnyModalOpen = showAddModal || showImportModal || showSatelliteModal || showSettingsModal || showNeighborhoodModal || showTestResultsModal;
    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [showAddModal, showImportModal, showSatelliteModal, showSettingsModal]);

  // Load alert criteria from API on mount
  useEffect(() => {
    fetch('/api/search-criteria')
      .then(res => res.json())
      .then(data => {
        if (data && !data.error) {
          setAlertCriteria(data);
        }
      })
      .catch(err => console.log('Could not load alert criteria:', err));
  }, []);

  // Dark mode effect - save preference and toggle body class
  useEffect(() => {
    localStorage.setItem('house-hunter-dark-mode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Tour steps configuration - comprehensive guide for new users
  const tourSteps = [
    {
      target: 'welcome',
      title: 'Welcome to House Hunter! 🏔️',
      content: 'This app helps you find Colorado Springs homes with the best mountain views. Let me show you how to use AI-powered satellite analysis to evaluate properties.',
      position: 'center'
    },
    {
      target: 'add-button',
      title: 'Step 1: Add a Property',
      content: 'Click the + button to add a property. Paste a Zillow or Redfin URL and the address auto-fills, or type manually. Pro tip: Install our Chrome extension for one-click imports!',
      position: 'bottom'
    },
    {
      target: 'ai-analysis',
      title: 'Step 2: Run AI Analysis 🛰️',
      content: 'This is the key feature! After adding a property, click "Analyze Location" in the edit form. Claude AI will examine satellite imagery to determine: house orientation, deck/patio direction, and mountain view potential.',
      position: 'bottom'
    },
    {
      target: 'welcome',
      title: 'What AI Analysis Tells You',
      content: 'The AI examines satellite photos to find: 1) Which way the house faces, 2) Where decks/patios are located, 3) If the back or deck faces west toward Pikes Peak. It rates view potential from "excellent" to "none".',
      position: 'center'
    },
    {
      target: 'filters',
      title: 'Step 3: Filter Your Properties',
      content: 'Use filters to narrow your search. Filter by status (New, Watch, Tour, Pass), neighborhood, or sort by price, view quality, or elevation.',
      position: 'bottom'
    },
    {
      target: 'stats',
      title: 'Track Your Progress',
      content: 'These stats show how many properties you have at each stage. Keep track as you move homes from "New" to "Watch" to "Tour".',
      position: 'bottom'
    },
    {
      target: 'done',
      title: 'You\'re Ready! 🎉',
      content: 'Add your first property, then click "Analyze Location" button in the edit form to see AI satellite analysis. Happy house hunting!',
      position: 'center'
    }
  ];

  // Check if first-time user
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('house-hunter-toured');
    if (!hasSeenTour) {
      setShowTour(true);
      setTourStep(0);
    }
  }, []);

  const nextTourStep = () => {
    if (tourStep < tourSteps.length - 1) {
      setTourStep(tourStep + 1);
    } else {
      completeTour();
    }
  };

  const prevTourStep = () => {
    if (tourStep > 0) {
      setTourStep(tourStep - 1);
    }
  };

  const completeTour = () => {
    localStorage.setItem('house-hunter-toured', 'true');
    setShowTour(false);
    setTourStep(0);
    analytics.tourComplete();
  };

  const skipTour = () => {
    analytics.tourSkip(tourStep);
    completeTour();
  };

  const restartTour = () => {
    setTourStep(0);
    setShowTour(true);
    analytics.tourStart();
  };

  // Fetch Colorado Springs weather
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Using Open-Meteo free API (no key required)
        // Colorado Springs coordinates: 38.8339, -104.8214
        const response = await fetch(
          'https://api.open-meteo.com/v1/forecast?latitude=38.8339&longitude=-104.8214&current=temperature_2m,weather_code&temperature_unit=fahrenheit&timezone=America/Denver'
        );
        const data = await response.json();
        if (data.current) {
          setWeather({
            temp: Math.round(data.current.temperature_2m),
            code: data.current.weather_code
          });
        }
      } catch (error) {
        console.log('Weather fetch failed:', error);
      }
    };
    fetchWeather();
    // Refresh weather every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Get weather emoji, description, and house-hunting tip based on WMO weather code
  const getWeatherDisplay = (code, temp) => {
    if (code === 0) return { emoji: '☀️', desc: 'Clear skies - Perfect day to see Pikes Peak!', tip: 'Great touring weather' };
    if (code === 1) return { emoji: '🌤️', desc: 'Mostly clear - Mountain views looking good', tip: 'Great touring weather' };
    if (code === 2) return { emoji: '⛅', desc: 'Partly cloudy - Peak might play hide and seek', tip: 'Good for tours' };
    if (code === 3) return { emoji: '☁️', desc: 'Overcast - Mountains hiding today', tip: 'Browse Zillow instead' };
    if (code >= 45 && code <= 48) return { emoji: '🌫️', desc: 'Foggy - Zero mountain visibility', tip: 'Stay home & browse' };
    if (code >= 51 && code <= 55) return { emoji: '🌧️', desc: 'Drizzle - Not ideal for house tours', tip: 'Couch & Zillow day' };
    if (code >= 56 && code <= 57) return { emoji: '🌨️', desc: 'Freezing drizzle - Roads sketchy', tip: 'Stay safe inside' };
    if (code >= 61 && code <= 65) return { emoji: '🌧️', desc: 'Rainy - Save tours for another day', tip: 'Online browsing day' };
    if (code >= 66 && code <= 67) return { emoji: '🌨️', desc: 'Freezing rain - Definitely stay in', tip: 'Dangerous out there' };
    if (code >= 71 && code <= 77) return { emoji: '❄️', desc: 'Snowy - Pikes Peak views will be stunning after!', tip: temp < 20 ? 'Bundle up if touring' : 'Fresh snow views!' };
    if (code >= 80 && code <= 82) return { emoji: '🌦️', desc: 'Showers - Wait for a break to tour', tip: 'Check radar first' };
    if (code >= 85 && code <= 86) return { emoji: '🌨️', desc: 'Snow showers - Mountain magic happening', tip: 'Cozy browsing time' };
    if (code >= 95) return { emoji: '⛈️', desc: 'Thunderstorm - Classic CO afternoon', tip: 'Stay indoors!' };
    return { emoji: '🏔️', desc: 'Mountain weather doing its thing', tip: 'Check conditions' };
  };

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });
    }
  }, []);

  // Handle shared URLs from mobile share target
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedUrl = params.get('shared');
    if (sharedUrl) {
      // Clean up URL and open add modal with the shared URL
      window.history.replaceState({}, '', '/');
      setListingUrl(sharedUrl);
      setShowAddModal(true);
      // Auto-fetch the listing data
      handleFetchFromUrl(sharedUrl);
    }
  }, []);

  // Fetch listing data from URL (extracts address from URL, then auto-detects elevation)
  const handleFetchFromUrl = async (url = listingUrl) => {
    if (!url) return;

    // Validate URL (redf.in is Redfin's short URL domain)
    if (!url.includes('zillow.com') && !url.includes('redfin.com') && !url.includes('redf.in')) {
      showToast({ type: 'error', message: 'Only Zillow and Redfin URLs are supported' }, 3000);
      return;
    }

    setFetchingUrl(true);
    try {
      const response = await fetch(`/api/scrape?url=${encodeURIComponent(url)}`);
      const result = await response.json();

      if (result.success && result.data) {
        const newListing = {
          ...emptyListing,
          ...result.data,
          link: url,
        };
        setCurrentListing(newListing);
        setListingUrl('');

        // Show appropriate message based on what we got
        if (result.data.address) {
          showToast({
            type: 'info',
            message: `Got address from URL. Fill in price/beds/baths manually, then use Auto-detect for elevation.`
          }, 5000);
        } else {
          showToast({ type: 'error', message: 'Could not parse address from URL. Enter it manually.' }, 3000);
        }
      } else {
        showToast({ type: 'error', message: result.error || 'Could not parse URL' }, 3000);
      }
    } catch (error) {
      console.error('Fetch URL error:', error);
      showToast({ type: 'error', message: 'Failed to fetch listing data' }, 3000);
    }
    setFetchingUrl(false);
  };

  // Show a toast notification
  const showToast = (content, duration = 4000) => {
    setToast(content);
    setTimeout(() => setToast(null), duration);
  };

  // Lookup elevation, orientation, AND satellite analysis for current address
  const handleLookupLocation = async () => {
    if (!currentListing.address) {
      alert('Enter an address first');
      return;
    }

    setLookingUp(true);
    setLookupStep('geocoding');

    try {
      // Step 1: Geocode the address
      const coords = await geocodeAddress(currentListing.address);
      if (!coords) {
        alert('Could not find address. Try adding more details.');
        setLookingUp(false);
        setLookupStep('');
        return;
      }

      console.log('Geocoded:', coords);

      // Extract and map neighborhood
      const detectedNeighborhood = mapNeighborhood(coords.neighborhood);
      if (coords.neighborhood) {
        console.log('Neighborhood detected:', coords.neighborhood, '→', detectedNeighborhood);
      }

      // Step 2: Get elevation
      setLookupStep('elevation');
      const elevationMeters = await getElevation(coords.lat, coords.lng);
      const elevationCategory = elevationToCategory(elevationMeters);
      const elevationFeet = elevationMeters ? Math.round(elevationMeters * 3.28084) : null;

      console.log('Elevation:', elevationMeters, 'm =', elevationFeet, 'ft =', elevationCategory);

      // Calculate orientation to mountains (direction from property to Pikes Peak)
      const orientation = calculateOrientation(coords.lat, coords.lng);
      console.log('Orientation to mountains:', orientation);

      // Update the form with elevation data and neighborhood
      setCurrentListing(prev => ({
        ...prev,
        elevation: elevationCategory,
        orientation: orientation,
        neighborhood: detectedNeighborhood || prev.neighborhood,
      }));

      // Step 3: Fetch and analyze satellite imagery
      setLookupStep('satellite');

      try {
        const response = await fetch('/api/analyze-satellite', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lat: coords.lat,
            lng: coords.lng,
            address: currentListing.address,
            peakBearing: orientation
          }),
        });

        const result = await response.json();

        if (result.success && result.analysis) {
          // Store satellite data and show modal
          setSatelliteData({
            ...result,
            coords,
            peakBearing: orientation,
            elevationFeet,
            elevationCategory
          });
          setShowSatelliteModal(true);
        } else {
          // Satellite failed, but we still have elevation - show that
          const mountainEmoji = {
            'Low': '🏠___/⛰️',
            'Mid': '🏠__/\\⛰️',
            'High': '🏠_/\\/⛰️',
            'Ridge': '🏠/\\/\\⛰️'
          };
          showToast({
            type: 'elevation',
            elevation: elevationCategory,
            feet: elevationFeet,
            orientation,
            mountain: mountainEmoji[elevationCategory],
            viewNote: '(Satellite analysis unavailable)'
          }, 5000);
        }
      } catch (satError) {
        console.error('Satellite analysis error:', satError);
        // Still show elevation info even if satellite fails
        const mountainEmoji = {
          'Low': '🏠___/⛰️',
          'Mid': '🏠__/\\⛰️',
          'High': '🏠_/\\/⛰️',
          'Ridge': '🏠/\\/\\⛰️'
        };
        showToast({
          type: 'elevation',
          elevation: elevationCategory,
          feet: elevationFeet,
          orientation,
          mountain: mountainEmoji[elevationCategory],
          viewNote: '(Satellite analysis unavailable)'
        }, 5000);
      }

    } catch (error) {
      console.error('Lookup error:', error);
      showToast({ type: 'error', message: 'Could not look up location' }, 3000);
    }

    setLookingUp(false);
    setLookupStep('');
  };

  // Analyze photo using Claude Vision API
  const handleAnalyzePhoto = async () => {
    if (!currentListing.photo) {
      showToast({ type: 'error', message: 'Upload a photo first' }, 3000);
      return;
    }

    setAnalyzingPhoto(true);
    setPhotoAnalysis(null);

    try {
      // Determine if photo is a URL or base64
      const isUrl = currentListing.photo.startsWith('http');
      const response = await fetch('/api/analyze-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isUrl ? { imageUrl: currentListing.photo } : { imageBase64: currentListing.photo }),
      });

      const result = await response.json();

      if (result.success && result.analysis) {
        setPhotoAnalysis(result.analysis);

        // Auto-fill view rating if we got a good analysis
        if (result.analysis.viewQuality && result.analysis.confidence !== 'low') {
          const viewMap = {
            'none': 'None',
            'partial': 'Partial',
            'good': 'Good',
            'excellent': 'Excellent'
          };
          const mappedView = viewMap[result.analysis.viewQuality] || currentListing.viewReality;

          setCurrentListing(prev => ({
            ...prev,
            viewReality: mappedView,
            notes: prev.notes + (prev.notes ? '\n' : '') + `[AI: ${result.analysis.viewDescription || ''}${result.analysis.facingDirection !== 'unknown' ? ` | Likely facing ${result.analysis.facingDirection.replace('likely_', '')}` : ''}]`
          }));
        }

        showToast({
          type: 'analysis',
          analysis: result.analysis,
          usage: result.usage
        }, 8000);
      } else {
        showToast({ type: 'error', message: result.error || 'Analysis failed' }, 3000);
      }
    } catch (error) {
      console.error('Photo analysis error:', error);
      showToast({ type: 'error', message: 'Failed to analyze photo' }, 3000);
    }

    setAnalyzingPhoto(false);
  };

  // Apply satellite analysis results to the listing
  const applySatelliteAnalysis = () => {
    const analysis = satelliteData.analysis;

    // Determine if the deck/back faces toward the mountains (W, SW, NW are good)
    const goodViewDirections = ['W', 'SW', 'NW'];
    const deckFacesGood = goodViewDirections.includes(analysis.deckFaces);
    const backFacesGood = goodViewDirections.includes(analysis.backFaces);
    const hasGoodViewPotential = deckFacesGood || backFacesGood;

    // Map mountain view potential to view rating
    const viewMap = {
      'excellent': 'Excellent',
      'likely': 'Good',
      'possible': 'Partial',
      'unlikely': 'Yard Only',
      'none': 'None'
    };

    // Determine the best orientation to save (prefer deck, then back)
    const bestOrientation = analysis.deckFaces !== 'unknown' ? analysis.deckFaces :
                           analysis.backFaces !== 'unknown' ? analysis.backFaces :
                           satelliteData.peakBearing;

    // Build a concise, readable note
    const viewEmoji = {
      'excellent': '🏔️✨',
      'likely': '🏔️',
      'possible': '⛰️',
      'unlikely': '🌲',
      'none': '❌'
    };

    // Build comprehensive notes
    const noteLines = [];

    // Line 1: Key metrics summary
    const summaryParts = [];
    if (satelliteData.elevationFeet) {
      summaryParts.push(`${satelliteData.elevationFeet.toLocaleString()}ft`);
    }
    summaryParts.push(`Front→${analysis.frontFaces}`);
    summaryParts.push(`Back→${analysis.backFaces}`);
    const viewEmoji2 = viewEmoji[analysis.mountainViewPotential] || '';
    summaryParts.push(`${viewEmoji2} ${analysis.mountainViewPotential} view`);
    noteLines.push(`AI: ${summaryParts.join(' | ')}`);

    // Line 2: Deck info if present
    if (analysis.deckLocation && analysis.deckLocation !== 'none_visible') {
      const deckInfo = `Deck: ${analysis.deckLocation.replace(/_/g, ' ')}`;
      const deckDirection = analysis.deckFaces !== 'unknown' ? `, faces ${analysis.deckFaces}` : '';
      const deckViewNote = ['W', 'SW', 'NW'].includes(analysis.deckFaces) ? ' (mountain views!)' : '';
      noteLines.push(deckInfo + deckDirection + deckViewNote);
    }

    // Line 3: Noise assessment
    if (analysis.noiseLevel) {
      const noiseEmoji = {
        'quiet': '🤫',
        'moderate': '🔉',
        'busy': '🔊',
        'loud': '📢'
      };
      let noiseLine = `Noise: ${noiseEmoji[analysis.noiseLevel] || ''} ${analysis.noiseLevel}`;
      if (analysis.noiseFactors && analysis.noiseFactors.length > 0) {
        const factors = analysis.noiseFactors.map(f => f.replace(/_/g, ' ')).join(', ');
        noiseLine += ` (${factors})`;
      }
      noteLines.push(noiseLine);
    }

    // Line 4: Full AI reasoning
    if (analysis.reasoning) {
      noteLines.push(`AI notes: ${analysis.reasoning}`);
    }

    const fullNote = noteLines.join('\n');

    setCurrentListing(prev => ({
      ...prev,
      lat: satelliteData.coords?.lat || prev.lat,
      lng: satelliteData.coords?.lng || prev.lng,
      elevation: satelliteData.elevationCategory || prev.elevation,
      orientation: bestOrientation,
      viewReality: viewMap[analysis.mountainViewPotential] || prev.viewReality,
      noiseLevel: analysis.noiseLevel || prev.noiseLevel,
      notes: prev.notes + (prev.notes ? '\n' : '') + fullNote
    }));

    setShowSatelliteModal(false);

    // Show a celebratory or informative toast
    if (hasGoodViewPotential) {
      showToast({
        type: 'info',
        message: `🎯 Great news! The ${deckFacesGood ? 'deck' : 'back yard'} faces ${deckFacesGood ? analysis.deckFaces : analysis.backFaces} toward Pikes Peak!`
      }, 5000);
    } else {
      showToast({
        type: 'info',
        message: `Analysis complete. Back faces ${analysis.backFaces} - mountains are to the ${satelliteData.peakBearing}.`
      }, 5000);
    }
  };

  // Clear stale localStorage on app startup to prevent false duplicate detection
  useEffect(() => {
    localStorage.removeItem('house-hunter-listings');
  }, []);

  // Load listings from Firestore with real-time updates
  useEffect(() => {
    console.log('Setting up Firestore listener...');

    const unsubscribe = onSnapshot(
      collection(db, LISTINGS_COLLECTION),
      (snapshot) => {
        console.log('Firestore snapshot received:', snapshot.docs.length, 'documents');
        const listingsData = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          id: docSnap.id, // Use Firestore document ID
        }));
        // Sort client-side to avoid needing a Firestore index
        listingsData.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
        setListings(listingsData);
        setLoading(false);
      },
      (error) => {
        console.error('Firestore listener error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load inbox items from Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'house-hunter-inbox'),
      (snapshot) => {
        const items = snapshot.docs.map(docSnap => ({
          ...docSnap.data(),
          id: docSnap.id,
        }));
        items.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
        setInboxItems(items);
        setInboxLoading(false);
      },
      (error) => {
        console.error('Inbox listener error:', error);
        setInboxLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Approve inbox item - move to main listings
  const handleApproveInbox = async (item) => {
    try {
      // Add to main listings
      const { id, ...listingData } = item;
      listingData.status = 'New';
      listingData.source = 'inbox-approved';
      await addDoc(collection(db, LISTINGS_COLLECTION), listingData);

      // Delete from inbox
      await deleteDoc(doc(db, 'house-hunter-inbox', id));

      setToast({ type: 'success', message: `Added ${item.address} to listings` });
    } catch (error) {
      console.error('Error approving inbox item:', error);
      setToast({ type: 'error', message: 'Failed to approve listing' });
    }
  };

  // Reject inbox item - delete from inbox
  const handleRejectInbox = async (item) => {
    try {
      await deleteDoc(doc(db, 'house-hunter-inbox', item.id));
      setToast({ type: 'success', message: `Removed ${item.address} from inbox` });
    } catch (error) {
      console.error('Error rejecting inbox item:', error);
      setToast({ type: 'error', message: 'Failed to remove listing' });
    }
  };

  const handleSave = async () => {
    if (!currentListing.address || !currentListing.price) return;

    setSaving(true);
    console.log('Saving listing to Firestore...');

    try {
      if (editingListing !== null) {
        // Update existing listing
        const listingId = listings[editingListing].id;
        console.log('Updating existing listing:', listingId);
        const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
        const { id, ...updateData } = currentListing;
        await updateDoc(listingRef, updateData);
        console.log('Listing updated successfully');
        analytics.listingEdit(currentListing.address);
      } else {
        // Add new listing
        const newListing = {
          ...currentListing,
          dateAdded: new Date().toISOString().split('T')[0],
        };
        console.log('Adding new listing:', newListing);
        const docRef = await addDoc(collection(db, LISTINGS_COLLECTION), newListing);
        console.log('Listing added with ID:', docRef.id);
        analytics.listingAdd();
      }

      setShowAddModal(false);
      setEditingListing(null);
      setCurrentListing({ ...emptyListing });
      setPhotoAnalysis(null);
      setHighlightAiAnalysis(false);
    } catch (error) {
      console.error('Error saving listing:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      alert(`Error saving listing: ${error.message}`);
    }
    setSaving(false);
  };

  const handleEdit = (index) => {
    setEditingListing(index);
    const listing = listings[index];
    setCurrentListing({ ...listing });
    // Highlight AI analysis button if property hasn't been analyzed yet
    if (!listing.elevation && listing.address) {
      setHighlightAiAnalysis(true);
    }
    setShowAddModal(true);
  };

  const handleDelete = async (index) => {
    if (window.confirm('Delete this listing?')) {
      try {
        const listingId = listings[index].id;
        const address = listings[index].address;
        await deleteDoc(doc(db, LISTINGS_COLLECTION, listingId));
        analytics.listingDelete(address);
      } catch (error) {
        console.error('Error deleting listing:', error);
        alert('Error deleting listing.');
      }
    }
  };

  const handleStatusChange = async (index, status) => {
    try {
      const listingId = listings[index].id;
      const address = listings[index].address;
      const listingRef = doc(db, LISTINGS_COLLECTION, listingId);
      await updateDoc(listingRef, { status });
      analytics.listingStatusChange(address, status);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Import listings from JSON file
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        const newListings = Array.isArray(imported) ? imported : [imported];

        console.log('Importing', newListings.length, 'listings from file');
        console.log('Current listings in state:', listings.length);

        // Merge with existing listings (avoid duplicates by address)
        const existingAddresses = new Set(listings.map(l => l.address?.toLowerCase()).filter(Boolean));
        console.log('Existing addresses:', Array.from(existingAddresses));

        const uniqueNew = newListings.filter(l => {
          const addr = l.address?.toLowerCase();
          const isDuplicate = existingAddresses.has(addr);
          if (isDuplicate) {
            console.log('Skipping duplicate:', l.address);
          }
          return !isDuplicate;
        });

        console.log('Unique new listings to import:', uniqueNew.length);

        if (uniqueNew.length > 0) {
          setImportStatus(`Importing ${uniqueNew.length} listings...`);

          // Add each listing to Firestore
          for (const listing of uniqueNew) {
            const { id, ...listingData } = listing;
            const newDoc = {
              ...emptyListing,
              ...listingData,
              dateAdded: listingData.dateAdded || new Date().toISOString().split('T')[0],
            };
            console.log('Adding to Firestore:', newDoc.address);
            const docRef = await addDoc(collection(db, LISTINGS_COLLECTION), newDoc);
            console.log('Added with ID:', docRef.id);
          }

          setImportStatus(`Imported ${uniqueNew.length} new listings!`);
          showToast({ type: 'import', count: uniqueNew.length }, 4000);
        } else {
          setImportStatus('No new listings to import (all duplicates)');
        }
      } catch (error) {
        console.error('Import error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        setImportStatus(`Error: ${error.message || 'Invalid JSON file'}`);
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Sync from webhook server
  const handleSyncFromServer = async () => {
    const webhookUrl = localStorage.getItem('house-hunter-webhook-url');
    if (!webhookUrl) {
      setImportStatus('Please set your webhook server URL first');
      return;
    }

    try {
      setImportStatus('Syncing...');
      const response = await fetch(`${webhookUrl}/api/listings`);
      if (!response.ok) throw new Error('Failed to fetch');

      const serverListings = await response.json();
      const existingAddresses = new Set(listings.map(l => l.address.toLowerCase()));
      const uniqueNew = serverListings.filter(l => !existingAddresses.has(l.address?.toLowerCase()));

      if (uniqueNew.length > 0) {
        // Add each listing to Firestore
        for (const listing of uniqueNew) {
          const { id, ...listingData } = listing;
          await addDoc(collection(db, LISTINGS_COLLECTION), {
            ...emptyListing,
            ...listingData,
            dateAdded: listingData.dateAdded || new Date().toISOString().split('T')[0],
          });
        }

        setImportStatus(`Synced ${uniqueNew.length} new listings from server!`);
      } else {
        setImportStatus('No new listings on server');
      }
    } catch (error) {
      console.error('Sync error:', error);
      setImportStatus('Error connecting to webhook server');
    }
  };

  // Save alert criteria
  const saveAlertCriteria = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch('/api/search-criteria', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(alertCriteria)
      });
      const data = await response.json();
      if (data.success) {
        showToast({ type: 'info', content: 'Alert settings saved!' });
        setShowSettingsModal(false);
      } else {
        showToast({ type: 'error', content: 'Failed to save settings' });
      }
    } catch (err) {
      console.error('Save settings error:', err);
      showToast({ type: 'error', content: 'Error saving settings' });
    }
    setSavingSettings(false);
  };

  // Test alert check manually
  const testAlertCheck = async () => {
    setRunningTest(true);
    showToast({ type: 'info', content: 'Running listing check... This may take a minute.' });
    try {
      const response = await fetch('/api/check-listings?test=true');
      const data = await response.json();
      setTestResults(data);
      setShowTestResultsModal(true);
      showToast({
        type: 'info',
        content: data.message || 'Check complete'
      });
    } catch (err) {
      showToast({ type: 'error', content: 'Error running check' });
    }
    setRunningTest(false);
  };

  // Filter and sort listings
  const filteredListings = listings
    .filter(l => activeFilter === 'All' || l.status === activeFilter)
    .sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return (parseInt(a.price) || 0) - (parseInt(b.price) || 0);
        case 'viewReality':
          return VIEW_RATINGS.indexOf(b.viewReality) - VIEW_RATINGS.indexOf(a.viewReality);
        case 'elevation':
          return ELEVATION_POSITIONS.indexOf(b.elevation) - ELEVATION_POSITIONS.indexOf(a.elevation);
        case 'monthly':
          return calculateMonthly(parseInt(a.price) || 0, 20, 6.5, 0.5, 150, parseInt(a.hoa) || 0) -
                 calculateMonthly(parseInt(b.price) || 0, 20, 6.5, 0.5, 150, parseInt(b.hoa) || 0);
        default:
          return new Date(b.dateAdded) - new Date(a.dateAdded);
      }
    });

  // Stats
  const stats = {
    total: listings.length,
    new: listings.filter(l => l.status === 'New').length,
    watch: listings.filter(l => l.status === 'Watch').length,
    tour: listings.filter(l => l.status === 'Tour').length,
    pass: listings.filter(l => l.status === 'Pass').length,
  };

  return (
    <div className={`min-h-screen p-4 md:p-8 transition-colors ${darkMode ? 'bg-dark text-white' : 'bg-gray-100 text-gray-900'}`} role="main">
      {/* Skip link for keyboard users */}
      <a
        href="#listings"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-dark focus:rounded-lg"
      >
        Skip to listings
      </a>

      {/* Header */}
      <header className="mb-8" role="banner">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <AiOutlineHome className="text-primary" />
              House Hunter
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded font-normal">
                Beta
              </span>
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-gray-400">Colorado Springs</p>
              {weather && (
                <div
                  className="flex items-center gap-1.5 px-2 py-0.5 bg-dark-card rounded-full border border-dark-border text-sm cursor-default"
                  title={getWeatherDisplay(weather.code, weather.temp).desc}
                >
                  <span>{getWeatherDisplay(weather.code, weather.temp).emoji}</span>
                  <span className="text-gray-300">{weather.temp}°F</span>
                  <span className="hidden sm:inline text-gray-500">· {getWeatherDisplay(weather.code, weather.temp).tip}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {/* Alert Settings */}
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 border border-dark-border text-gray-400 hover:text-white rounded-lg transition-colors"
              title="Alert settings"
            >
              <AiOutlineBell />
            </button>
            {/* Dark/Light mode toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 border border-dark-border text-gray-400 hover:text-white rounded-lg transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <BsSun /> : <BsMoon />}
            </button>
            {/* Map view toggle */}
            {listings.length > 0 && (
              <button
                onClick={() => setShowMapView(!showMapView)}
                className={`p-2 border rounded-lg transition-colors ${
                  showMapView
                    ? 'border-primary bg-primary/20 text-primary'
                    : 'border-dark-border text-gray-400 hover:text-white'
                }`}
                title={showMapView ? 'Show list view' : 'Show map view'}
              >
                <BsMap />
              </button>
            )}
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-dark-border text-gray-400 hover:text-white rounded-lg transition-colors"
              title="Import listings"
              data-tour="import"
            >
              <AiOutlineUpload />
              <span className="hidden sm:inline">Import</span>
            </button>
            {listings.length > 0 && (
              <button
                onClick={() => exportToCSV(listings)}
                className="flex items-center gap-2 px-4 py-2 border border-dark-border text-gray-400 hover:text-white rounded-lg transition-colors"
                title="Export to CSV"
              >
                <AiOutlineDownload />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            <button
              onClick={() => {
                setCurrentListing({ ...emptyListing });
                setEditingListing(null);
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-dark font-medium rounded-lg hover:bg-primary-hover transition-colors"
              data-tour="add-button"
              aria-label="Add new listing"
            >
              <AiOutlinePlus aria-hidden="true" />
              <span className="hidden sm:inline">Add Listing</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs - Listings / Inbox */}
      <div className="flex gap-4 mb-6 border-b border-dark-border">
        <button
          onClick={() => setActiveTab('listings')}
          className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
            activeTab === 'listings'
              ? 'text-primary'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          My Listings
          {activeTab === 'listings' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('inbox')}
          className={`pb-3 px-1 text-sm font-medium transition-colors relative flex items-center gap-2 ${
            activeTab === 'inbox'
              ? 'text-primary'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Inbox
          {inboxItems.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-primary text-dark rounded-full font-bold">
              {inboxItems.length}
            </span>
          )}
          {activeTab === 'inbox' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      </div>

      {/* Stats - only show on listings tab */}
      {activeTab === 'listings' && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-tour="stats">
        {[
          { label: 'Total', value: stats.total, color: 'text-white' },
          { label: 'New', value: stats.new, color: 'text-blue-400' },
          { label: 'Watch', value: stats.watch, color: 'text-yellow-400' },
          { label: 'Tour', value: stats.tour, color: 'text-purple-400' },
          { label: 'Pass', value: stats.pass, color: 'text-gray-400' },
        ].map(stat => (
          <div key={stat.label} className="bg-dark-card rounded-lg p-4 border border-dark-border">
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-gray-500 text-sm">{stat.label}</div>
          </div>
        ))}
      </div>
      )}

      {/* Filters & Sort - only show on listings tab */}
      {activeTab === 'listings' && (
      <div className="flex flex-wrap items-center gap-4 mb-6" data-tour="filters">
        <div className="flex gap-2">
          {['All', ...STATUSES].map(status => (
            <button
              key={status}
              onClick={() => setActiveFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                activeFilter === status
                  ? 'bg-primary text-dark font-medium'
                  : 'bg-dark-card border border-dark-border text-gray-400 hover:text-white'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value);
            analytics.sortChange(e.target.value);
          }}
          className="px-3 py-1.5 bg-dark-card border border-dark-border rounded-lg text-sm"
          aria-label="Sort listings by"
        >
          <option value="dateAdded">Newest First</option>
          <option value="price">Price (Low to High)</option>
          <option value="monthly">Monthly (Low to High)</option>
          <option value="viewReality">Best View</option>
          <option value="elevation">Highest Elevation</option>
        </select>
      </div>
      )}

      {/* Inbox View */}
      {activeTab === 'inbox' && (
        <div className="space-y-4">
          {inboxLoading ? (
            <div className="text-center py-20 text-gray-500">
              <AiOutlineLoading3Quarters className="text-6xl mx-auto mb-4 animate-spin text-primary" />
              <p>Loading inbox...</p>
            </div>
          ) : inboxItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">📬</div>
              <h3 className="text-xl font-semibold text-white mb-2">Inbox empty</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                New listings from auto-alerts will appear here for your review before being added to your main list.
              </p>
            </div>
          ) : (
            <>
              <p className="text-gray-400 text-sm mb-4">
                Review new listings found by auto-alerts. Approve to add to your main list or reject to dismiss.
              </p>
              {inboxItems.map(item => (
                <div key={item.id} className="bg-dark-card rounded-lg p-4 border border-dark-border">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{item.address}</h3>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-400 mt-1">
                        <span>${parseInt(item.price || 0).toLocaleString()}</span>
                        <span>{item.beds} bed</span>
                        <span>{item.baths} bath</span>
                        <span>{parseInt(item.sqft || 0).toLocaleString()} sqft</span>
                        {item.elevation && <span>Elevation: {item.elevation}</span>}
                      </div>
                      {item.link && (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline mt-2 inline-flex items-center gap-1">
                          <AiOutlineLink /> View listing
                        </a>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleApproveInbox(item)}
                        className="p-2 bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg hover:bg-green-500/30 transition-colors"
                        title="Add to listings"
                      >
                        <AiOutlineCheck />
                      </button>
                      <button
                        onClick={() => handleRejectInbox(item)}
                        className="p-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-colors"
                        title="Dismiss"
                      >
                        <AiOutlineClose />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Listings Grid or Map View - only show on listings tab */}
      {activeTab === 'listings' && (
      <section id="listings" aria-label="Property listings" tabIndex={-1}>
      {loading ? (
        <div className="text-center py-20 text-gray-500">
          <AiOutlineLoading3Quarters className="text-6xl mx-auto mb-4 animate-spin text-primary" />
          <p>Loading listings...</p>
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🏔️</div>
          <h3 className="text-xl font-semibold text-white mb-2">No listings yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Start tracking Colorado Springs properties. Add listings from Zillow or Redfin to compare views, elevation, and monthly costs.
          </p>
          <button
            onClick={() => {
              setCurrentListing({ ...emptyListing });
              setEditingListing(null);
              setShowAddModal(true);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-dark font-medium rounded-lg hover:bg-primary-hover transition-colors"
          >
            <AiOutlinePlus />
            Add Your First Listing
          </button>
          <p className="text-gray-500 text-sm mt-4">
            Tip: On mobile, share a listing directly from Zillow/Redfin app
          </p>
        </div>
      ) : showMapView ? (
        /* Map View with Leaflet */
        <div className="bg-dark-card rounded-xl border border-dark-border overflow-hidden">
          {(() => {
            const listingsWithCoords = filteredListings.filter(l => l.lat && l.lng);
            const listingsWithoutCoords = filteredListings.filter(l => !l.lat || !l.lng);

            if (listingsWithCoords.length === 0) {
              return (
                <div className="p-8 text-center">
                  <div className="text-4xl mb-4">🗺️</div>
                  <h3 className="text-lg font-semibold text-white mb-2">No mapped properties yet</h3>
                  <p className="text-gray-400 mb-4">
                    Run AI analysis on your properties to get their coordinates and see them on the map.
                  </p>
                  <p className="text-gray-500 text-sm">
                    {listingsWithoutCoords.length} listing(s) need coordinates
                  </p>
                </div>
              );
            }

            // Calculate center from all listings with coordinates
            const centerLat = listingsWithCoords.reduce((sum, l) => sum + parseFloat(l.lat), 0) / listingsWithCoords.length;
            const centerLng = listingsWithCoords.reduce((sum, l) => sum + parseFloat(l.lng), 0) / listingsWithCoords.length;

            return (
              <>
                <MapContainer
                  center={[centerLat, centerLng]}
                  zoom={12}
                  style={{ height: '500px', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {listingsWithCoords.map((listing) => {
                    const markerColor =
                      listing.viewReality === 'Excellent' || listing.viewReality === 'Good' ? 'green' :
                      listing.viewReality === 'Partial' || listing.viewReality === 'Possible' ? 'gold' : 'blue';

                    return (
                      <Marker
                        key={listing.id}
                        position={[parseFloat(listing.lat), parseFloat(listing.lng)]}
                        icon={createMarkerIcon(markerColor)}
                      >
                        <Popup>
                          <div className="text-sm min-w-[180px]">
                            <strong className="text-base">{listing.address}</strong>
                            <div className="text-lg font-bold text-green-600 my-1">
                              ${parseInt(listing.price).toLocaleString()}
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs my-2">
                              <span className="text-gray-500">View:</span>
                              <span className="font-medium">{listing.viewReality || '—'} {listing.viewReality === 'Excellent' || listing.viewReality === 'Good' ? '⛰️' : ''}</span>
                              <span className="text-gray-500">Back faces:</span>
                              <span className="font-medium">{listing.orientation || '—'}</span>
                              <span className="text-gray-500">Noise:</span>
                              <span className="font-medium">
                                {listing.noiseLevel === 'quiet' && '🤫 Quiet'}
                                {listing.noiseLevel === 'moderate' && '🔉 Moderate'}
                                {listing.noiseLevel === 'busy' && '🔊 Busy'}
                                {!listing.noiseLevel && '—'}
                              </span>
                              <span className="text-gray-500">Elevation:</span>
                              <span className="font-medium">{listing.elevation || '—'}</span>
                            </div>
                            <button
                              onClick={() => {
                                const idx = listings.findIndex(l => l.id === listing.id);
                                if (idx !== -1) {
                                  setShowMapView(false);
                                  handleEdit(idx);
                                }
                              }}
                              className="mt-2 px-3 py-1.5 bg-blue-500 text-white rounded text-xs w-full"
                            >
                              View Details
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
                <div className="p-4 border-t border-dark-border">
                  <div className="flex flex-wrap gap-4 mb-2">
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500 font-medium">View:</span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span> Good+
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span> Partial
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span> Other
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs text-gray-500 font-medium">Noise:</span>
                      <span className="text-xs text-gray-400">🤫 Quiet</span>
                      <span className="text-xs text-gray-400">🔉 Moderate</span>
                      <span className="text-xs text-gray-400">🔊 Busy</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Click markers for details Zillow/Redfin don't show: noise level, orientation, elevation
                  </p>
                  {listingsWithoutCoords.length > 0 && (
                    <p className="text-yellow-500/80 text-xs">
                      {listingsWithoutCoords.length} listing(s) missing coordinates - run AI analysis to add them
                    </p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredListings.map((listing, index) => {
            const originalIndex = listings.findIndex(l => l.id === listing.id);
            const monthly = calculateMonthly(
              parseInt(listing.price) || 0,
              20,
              6.5,
              0.5,
              150,
              parseInt(listing.hoa) || 0
            );

            return (
              <div
                key={listing.id}
                className="bg-dark-card rounded-xl border border-dark-border overflow-hidden hover:border-primary/30 transition-colors"
              >
                {/* Photo - clickable to open listing URL */}
                {listing.photo && (
                  <a
                    href={listing.link || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block w-full h-40 bg-dark ${listing.link ? 'cursor-pointer' : 'cursor-default'}`}
                    onClick={(e) => {
                      if (!listing.link) {
                        e.preventDefault();
                        handleEdit(originalIndex);
                      }
                    }}
                    aria-label={`View ${listing.address} listing`}
                  >
                    <img
                      src={listing.photo}
                      alt={listing.address}
                      className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                    />
                  </a>
                )}

                {/* Clickable card area - opens edit modal */}
                <div
                  className="cursor-pointer"
                  onClick={() => handleEdit(originalIndex)}
                >

                  <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-white truncate">{listing.address}</h3>
                    <div className="flex items-center gap-2">
                      <p className="text-primary font-bold text-lg">
                        ${parseInt(listing.price).toLocaleString()}
                      </p>
                      {/* Price drop indicator */}
                      {listing.originalPrice && parseInt(listing.originalPrice) > parseInt(listing.price) && (
                        <span className="text-xs text-green-400 flex items-center gap-0.5">
                          ↓ ${((parseInt(listing.originalPrice) - parseInt(listing.price)) / 1000).toFixed(0)}k
                        </span>
                      )}
                    </div>
                    {/* Days on market */}
                    {listing.daysOnMarket && (
                      <p className="text-gray-500 text-xs">{listing.daysOnMarket} days on market</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {/* Listing status (Active/Pending/Sold) */}
                    {listing.listingStatus && listing.listingStatus !== 'Unknown' && (
                      <span className={`px-2 py-0.5 text-[10px] rounded border ${LISTING_STATUS_COLORS[listing.listingStatus] || LISTING_STATUS_COLORS.Unknown}`}>
                        {listing.listingStatus}
                      </span>
                    )}
                    {/* Your status (New/Watch/Tour/Pass) */}
                    <span className={`px-2 py-1 text-xs rounded border ${STATUS_COLORS[listing.status]}`}>
                      {listing.status}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-3 gap-2 text-sm mb-3">
                  <div className="text-gray-400">
                    <span className="text-white">{listing.beds}</span> bed
                  </div>
                  <div className="text-gray-400">
                    <span className="text-white">{listing.baths}</span> bath
                  </div>
                  <div className="text-gray-400">
                    <span className="text-white">{parseInt(listing.sqft).toLocaleString()}</span> sqft
                  </div>
                </div>

                {/* Property Assessment - Top Row: View, Noise, Elevation */}
                <div className="grid grid-cols-3 gap-2 text-sm mb-2 pt-3 border-t border-dark-border">
                  {/* View Potential */}
                  <div className="flex items-center gap-1.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-base ${
                      listing.viewReality === 'Excellent' ? 'bg-green-500/20' :
                      listing.viewReality === 'Good' ? 'bg-emerald-500/20' :
                      listing.viewReality === 'Possible' ? 'bg-yellow-500/20' :
                      'bg-gray-500/20'
                    }`}>
                      {listing.viewReality === 'Excellent' && '🏔️'}
                      {listing.viewReality === 'Good' && '⛰️'}
                      {listing.viewReality === 'Possible' && '🌄'}
                      {(listing.viewReality === 'None' || listing.viewReality === 'Unlikely') && '🏠'}
                    </div>
                    <div>
                      <div className="text-gray-500 text-[9px] uppercase tracking-wider">View</div>
                      <div className={`text-xs font-medium ${
                        listing.viewReality === 'Excellent' || listing.viewReality === 'Good' ? 'text-green-400' : 'text-gray-300'
                      }`}>
                        {listing.viewReality}
                      </div>
                    </div>
                  </div>

                  {/* Noise Level */}
                  <div className="flex items-center gap-1.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-base ${
                      listing.noiseLevel === 'quiet' ? 'bg-green-500/20' :
                      listing.noiseLevel === 'moderate' ? 'bg-yellow-500/20' :
                      listing.noiseLevel === 'busy' ? 'bg-orange-500/20' :
                      listing.noiseLevel === 'loud' ? 'bg-red-500/20' :
                      'bg-gray-500/20'
                    }`}>
                      {listing.noiseLevel === 'quiet' && '🤫'}
                      {listing.noiseLevel === 'moderate' && '🔉'}
                      {listing.noiseLevel === 'busy' && '🔊'}
                      {listing.noiseLevel === 'loud' && '📢'}
                      {!listing.noiseLevel && '—'}
                    </div>
                    <div>
                      <div className="text-gray-500 text-[9px] uppercase tracking-wider">Noise</div>
                      <div className={`text-xs font-medium ${
                        listing.noiseLevel === 'quiet' ? 'text-green-400' :
                        listing.noiseLevel === 'moderate' ? 'text-yellow-400' :
                        listing.noiseLevel === 'busy' ? 'text-orange-400' :
                        listing.noiseLevel === 'loud' ? 'text-red-400' :
                        'text-gray-500'
                      }`}>
                        {listing.noiseLevel ? listing.noiseLevel.charAt(0).toUpperCase() + listing.noiseLevel.slice(1) : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Elevation */}
                  <div className="flex items-center gap-1.5">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-base ${
                      listing.elevation === 'Ridge' ? 'bg-green-500/20' :
                      listing.elevation === 'High' ? 'bg-emerald-500/20' :
                      'bg-gray-500/20'
                    }`}>
                      {listing.elevation === 'Ridge' && '🗻'}
                      {listing.elevation === 'High' && '⛰️'}
                      {listing.elevation === 'Mid' && '🏔️'}
                      {listing.elevation === 'Low' && '🏠'}
                    </div>
                    <div>
                      <div className="text-gray-500 text-[9px] uppercase tracking-wider">Elevation</div>
                      <div className={`text-xs font-medium ${
                        listing.elevation === 'High' || listing.elevation === 'Ridge' ? 'text-green-400' : 'text-gray-300'
                      }`}>
                        {listing.elevation}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Orientation Row: Front Faces & Back Faces */}
                <div className="grid grid-cols-2 gap-3 text-sm mb-3 pb-3 border-b border-dark-border">
                  {/* Front Faces */}
                  <div className="flex items-center gap-2">
                    <div className="relative w-8 h-8 rounded-lg bg-dark-card border border-dark-border flex items-center justify-center">
                      {/* Mini compass */}
                      <span className="absolute text-[6px] text-gray-500" style={{top: '2px'}}>N</span>
                      <span className="absolute text-[6px] text-cyan-400" style={{left: '3px'}}>W</span>
                      <div
                        className="w-2.5 h-2.5 flex items-center justify-center"
                        style={{
                          transform: `rotate(${
                            // Front faces is opposite of back (orientation)
                            listing.orientation === 'N' ? 180 :
                            listing.orientation === 'NE' ? 225 :
                            listing.orientation === 'E' ? 270 :
                            listing.orientation === 'SE' ? 315 :
                            listing.orientation === 'S' ? 0 :
                            listing.orientation === 'SW' ? 45 :
                            listing.orientation === 'W' ? 90 :
                            listing.orientation === 'NW' ? 135 : 0
                          }deg)`
                        }}
                      >
                        <div className={`w-0.5 h-2.5 rounded-full ${
                          ['E', 'NE', 'SE'].includes(listing.orientation)
                            ? 'bg-gradient-to-t from-cyan-500 to-cyan-300'
                            : 'bg-gradient-to-t from-gray-500 to-gray-400'
                        }`} />
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[10px] uppercase tracking-wider">Front Faces</div>
                      <div className={`font-medium flex items-center gap-1 ${
                        ['E', 'NE', 'SE'].includes(listing.orientation) ? 'text-cyan-400' : 'text-gray-300'
                      }`}>
                        {/* Compute opposite direction - front faces mountains when back is E/NE/SE */}
                        {listing.orientation === 'N' ? 'S' :
                         listing.orientation === 'NE' ? 'SW' :
                         listing.orientation === 'E' ? 'W' :
                         listing.orientation === 'SE' ? 'NW' :
                         listing.orientation === 'S' ? 'N' :
                         listing.orientation === 'SW' ? 'NE' :
                         listing.orientation === 'W' ? 'E' :
                         listing.orientation === 'NW' ? 'SE' : '—'}
                        {['E', 'NE', 'SE'].includes(listing.orientation) && <span className="text-xs">🏔️</span>}
                      </div>
                    </div>
                  </div>

                  {/* Back Faces */}
                  <div className="flex items-center gap-2">
                    <div className="relative w-8 h-8 rounded-lg bg-dark-card border border-dark-border flex items-center justify-center">
                      {/* Mini compass */}
                      <span className="absolute text-[6px] text-gray-500" style={{top: '2px'}}>N</span>
                      <span className="absolute text-[6px] text-cyan-400" style={{left: '3px'}}>W</span>
                      <div
                        className="w-2.5 h-2.5 flex items-center justify-center"
                        style={{
                          transform: `rotate(${
                            listing.orientation === 'N' ? 0 :
                            listing.orientation === 'NE' ? 45 :
                            listing.orientation === 'E' ? 90 :
                            listing.orientation === 'SE' ? 135 :
                            listing.orientation === 'S' ? 180 :
                            listing.orientation === 'SW' ? 225 :
                            listing.orientation === 'W' ? 270 :
                            listing.orientation === 'NW' ? 315 : 0
                          }deg)`
                        }}
                      >
                        <div className={`w-0.5 h-2.5 rounded-full ${
                          ['W', 'SW', 'NW'].includes(listing.orientation)
                            ? 'bg-gradient-to-t from-cyan-500 to-cyan-300'
                            : 'bg-gradient-to-t from-gray-500 to-gray-400'
                        }`} />
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-[10px] uppercase tracking-wider">Back Faces</div>
                      <div className={`font-medium flex items-center gap-1 ${
                        ['W', 'SW', 'NW'].includes(listing.orientation) ? 'text-cyan-400' : 'text-gray-300'
                      }`}>
                        {listing.orientation}
                        {['W', 'SW', 'NW'].includes(listing.orientation) && <span className="text-xs">🏔️</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly - hidden on mobile */}
                <div className="hidden sm:flex items-center justify-between mb-3">
                  <span className="text-gray-500 text-sm">Est. Monthly</span>
                  <span className="text-white font-medium">${monthly.toLocaleString()}/mo</span>
                </div>

                {/* Notes - hidden on mobile */}
                {listing.notes && (
                  <p className="hidden sm:block text-gray-400 text-sm mb-3 line-clamp-2">{listing.notes}</p>
                )}
                  </div>
                </div>

                {/* Actions - not clickable, separate from card body */}
                <div className="flex items-center justify-between pt-3 px-5 pb-5 border-t border-dark-border">
                  <div className="flex gap-2">
                    {listing.link && (
                      <a
                        href={listing.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-primary transition-colors"
                        title="View Listing"
                      >
                        <AiOutlineLink />
                      </a>
                    )}
                    <a
                      href={`https://www.google.com/maps/search/${encodeURIComponent(listing.address + ' Colorado Springs CO')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-primary transition-colors"
                      title="View on Map"
                    >
                      <AiOutlineEnvironment />
                    </a>
                    {NEIGHBORHOOD_INFO[listing.neighborhood] && (
                      <button
                        onClick={() => {
                          setNeighborhoodContent({ name: listing.neighborhood, ...NEIGHBORHOOD_INFO[listing.neighborhood] });
                          setShowNeighborhoodModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-primary transition-colors"
                        title={`${listing.neighborhood} neighborhood info`}
                      >
                        <AiOutlineInfoCircle />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(originalIndex)}
                      className="p-2 text-gray-400 hover:text-primary transition-colors"
                      title="Edit"
                    >
                      <AiOutlineEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(originalIndex)}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <AiOutlineDelete />
                    </button>
                  </div>

                  {/* Quick Status */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleStatusChange(originalIndex, 'Watch')}
                      className={`p-1.5 rounded ${listing.status === 'Watch' ? 'bg-yellow-500/20 text-yellow-400' : 'text-gray-500 hover:text-yellow-400'}`}
                      title="Watch"
                    >
                      <AiOutlineEye size={16} />
                    </button>
                    <button
                      onClick={() => handleStatusChange(originalIndex, 'Tour')}
                      className={`p-1.5 rounded ${listing.status === 'Tour' ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-purple-400'}`}
                      title="Tour"
                    >
                      <AiOutlineCheck size={16} />
                    </button>
                    <button
                      onClick={() => handleStatusChange(originalIndex, 'Pass')}
                      className={`p-1.5 rounded ${listing.status === 'Pass' ? 'bg-gray-500/20 text-gray-400' : 'text-gray-500 hover:text-gray-400'}`}
                      title="Pass"
                    >
                      <AiOutlineClose size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </section>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-card rounded-xl border border-dark-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-dark-border">
              <h2 className="text-xl font-semibold">
                {editingListing !== null ? 'Edit Listing' : 'Add New Listing'}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Add from URL */}
              {editingListing === null && (
                <div className="space-y-3 pb-4 border-b border-dark-border bg-primary/5 -mx-6 px-6 py-4 -mt-6 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📱</span>
                    <h3 className="text-sm font-medium text-white">Paste Listing URL</h3>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={listingUrl}
                      onChange={(e) => setListingUrl(e.target.value)}
                      className="flex-1 px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none text-sm"
                      placeholder="https://zillow.com/... or https://redfin.com/..."
                    />
                    <button
                      type="button"
                      onClick={() => handleFetchFromUrl()}
                      disabled={!listingUrl || fetchingUrl}
                      className="px-4 py-2 bg-primary text-dark font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {fetchingUrl ? (
                        <>
                          <AiOutlineLoading3Quarters className="animate-spin" />
                          Fetching...
                        </>
                      ) : (
                        <>
                          <AiOutlineLink />
                          Get Address
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">Copy a URL from Zillow or Redfin to auto-fill the address. You'll need to enter price manually.</p>
                </div>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Basic Info</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Address <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={currentListing.address}
                      onChange={(e) => setCurrentListing({ ...currentListing, address: e.target.value })}
                      className={`w-full px-3 py-2 bg-dark border rounded-lg focus:border-primary focus:outline-none ${
                        !currentListing.address ? 'border-red-500/50' : 'border-dark-border'
                      }`}
                      placeholder="123 Mountain View Dr"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Price <span className="text-red-400">*</span></label>
                    <input
                      type="number"
                      value={currentListing.price}
                      onChange={(e) => setCurrentListing({ ...currentListing, price: e.target.value })}
                      className={`w-full px-3 py-2 bg-dark border rounded-lg focus:border-primary focus:outline-none ${
                        !currentListing.price ? 'border-red-500/50' : 'border-dark-border'
                      }`}
                      placeholder="450000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">HOA (monthly)</label>
                    <input
                      type="number"
                      value={currentListing.hoa}
                      onChange={(e) => setCurrentListing({ ...currentListing, hoa: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Beds</label>
                    <input
                      type="number"
                      value={currentListing.beds}
                      onChange={(e) => setCurrentListing({ ...currentListing, beds: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Baths</label>
                    <input
                      type="number"
                      step="0.5"
                      value={currentListing.baths}
                      onChange={(e) => setCurrentListing({ ...currentListing, baths: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Sq Ft</label>
                    <input
                      type="number"
                      value={currentListing.sqft}
                      onChange={(e) => setCurrentListing({ ...currentListing, sqft: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="2000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Lot Size (acres)</label>
                    <input
                      type="text"
                      value={currentListing.lotSize}
                      onChange={(e) => setCurrentListing({ ...currentListing, lotSize: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="0.25"
                    />
                  </div>
                </div>
              </div>

              {/* View Assessment */}
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">View Assessment</h3>
                    <p className="text-xs text-gray-500 mt-1">How good is the mountain view from this property?</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setHighlightAiAnalysis(false);
                      handleLookupLocation();
                    }}
                    disabled={!currentListing.address || lookingUp}
                    className={`flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 text-blue-400 rounded-lg hover:from-blue-500/30 hover:to-cyan-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[220px] ${highlightAiAnalysis ? 'ring-2 ring-primary ring-offset-2 ring-offset-dark-card animate-pulse' : ''}`}
                    title="Finds GPS coordinates, elevation, and uses AI satellite analysis to determine house orientation"
                    data-tour="ai-analysis"
                  >
                    {lookingUp ? (
                      <div className="flex items-center gap-2">
                        <AiOutlineLoading3Quarters className="animate-spin" />
                        <span className="text-left">
                          {lookupStep === 'geocoding' && '📍 Finding address...'}
                          {lookupStep === 'elevation' && '⛰️ Getting elevation...'}
                          {lookupStep === 'satellite' && '🛰️ AI analyzing satellite...'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <AiOutlineEnvironment />
                        🛰️ AI Location Analysis
                      </>
                    )}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">What the listing claims</label>
                    <input
                      type="text"
                      value={currentListing.viewClaim}
                      onChange={(e) => setCurrentListing({ ...currentListing, viewClaim: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder='e.g. "Stunning mountain views!", "Pikes Peak panorama"'
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Actual View Quality</label>
                    <select
                      value={currentListing.viewReality}
                      onChange={(e) => setCurrentListing({ ...currentListing, viewReality: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                    >
                      <option value="None">None - No mountain view</option>
                      <option value="Yard Only">Yard Only - Trees/houses block it</option>
                      <option value="Partial">Partial - Some peaks visible</option>
                      <option value="Good">Good - Nice mountain backdrop</option>
                      <option value="Excellent">Excellent - Unobstructed Pikes Peak</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Elevation vs Mountains</label>
                    <select
                      value={currentListing.elevation}
                      onChange={(e) => setCurrentListing({ ...currentListing, elevation: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                    >
                      <option value="Low">Low - Valley/flat area 🏠___/⛰️</option>
                      <option value="Mid">Mid - Some elevation 🏠__/\⛰️</option>
                      <option value="High">High - Foothills 🏠_/\/⛰️</option>
                      <option value="Ridge">Ridge - Elevated position 🏠/\/\⛰️</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Direction to Mountains</label>
                    <select
                      value={currentListing.orientation}
                      onChange={(e) => setCurrentListing({ ...currentListing, orientation: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                    >
                      <option value="W">W - West (best for Pikes Peak)</option>
                      <option value="NW">NW - Northwest</option>
                      <option value="SW">SW - Southwest</option>
                      <option value="N">N - North</option>
                      <option value="S">S - South</option>
                      <option value="NE">NE - Northeast</option>
                      <option value="E">E - East (away from mountains)</option>
                      <option value="SE">SE - Southeast</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Neighborhood</label>
                    <div className="flex gap-2">
                      <select
                        value={currentListing.neighborhood}
                        onChange={(e) => setCurrentListing({ ...currentListing, neighborhood: e.target.value })}
                        className="flex-1 px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      >
                        {NEIGHBORHOODS.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      {NEIGHBORHOOD_INFO[currentListing.neighborhood] && (
                        <button
                          type="button"
                          onClick={() => {
                            setNeighborhoodContent({ name: currentListing.neighborhood, ...NEIGHBORHOOD_INFO[currentListing.neighborhood] });
                            setShowNeighborhoodModal(true);
                          }}
                          className="px-3 py-2 bg-dark border border-dark-border rounded-lg text-gray-400 hover:text-primary hover:border-primary/50 transition-colors"
                          title={`View ${currentListing.neighborhood} neighborhood info`}
                        >
                          <AiOutlineInfoCircle size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Photo */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Photo</h3>
                  {currentListing.photo && (
                    <button
                      type="button"
                      onClick={handleAnalyzePhoto}
                      disabled={analyzingPhoto}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Use AI to analyze this photo for mountain views"
                    >
                      {analyzingPhoto ? (
                        <>
                          <AiOutlineLoading3Quarters className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <AiOutlineEye />
                          AI Analyze View
                        </>
                      )}
                    </button>
                  )}
                </div>
                <div>
                  {currentListing.photo ? (
                    <div className="relative">
                      <img
                        src={currentListing.photo}
                        alt="Listing preview"
                        className="w-full h-48 object-cover rounded-lg border border-dark-border"
                      />
                      <button
                        onClick={() => { setCurrentListing({ ...currentListing, photo: '' }); setPhotoAnalysis(null); }}
                        className="absolute top-2 right-2 p-2 bg-dark/80 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                        title="Remove photo"
                      >
                        <AiOutlineClose />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-dark-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                      <AiOutlineCamera className="text-3xl text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setCurrentListing({ ...currentListing, photo: reader.result });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Status & Notes */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Status & Notes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Your Status</label>
                    <select
                      value={currentListing.status}
                      onChange={(e) => setCurrentListing({ ...currentListing, status: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Listing Status</label>
                    <select
                      value={currentListing.listingStatus || 'Active'}
                      onChange={(e) => setCurrentListing({ ...currentListing, listingStatus: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                    >
                      {LISTING_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Original Price</label>
                    <input
                      type="number"
                      value={currentListing.originalPrice}
                      onChange={(e) => setCurrentListing({ ...currentListing, originalPrice: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="If price dropped, enter original"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Days on Market</label>
                    <input
                      type="number"
                      value={currentListing.daysOnMarket}
                      onChange={(e) => setCurrentListing({ ...currentListing, daysOnMarket: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="e.g. 14"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Zillow/Redfin Link</label>
                    <input
                      type="url"
                      value={currentListing.link}
                      onChange={(e) => setCurrentListing({ ...currentListing, link: e.target.value })}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      placeholder="https://zillow.com/..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-400 mb-1">Notes</label>
                    <textarea
                      value={currentListing.notes}
                      onChange={(e) => setCurrentListing({ ...currentListing, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none resize-none"
                      placeholder="Flat lot, no elevation advantage, busy road..."
                    />
                  </div>
                </div>
              </div>

              {/* Monthly Estimate Preview */}
              {currentListing.price && (
                <div className="bg-dark rounded-lg p-4 border border-dark-border">
                  <div className="text-sm text-gray-400 mb-1">Estimated Monthly Payment</div>
                  <div className="text-2xl font-bold text-primary">
                    ${calculateMonthly(
                      parseInt(currentListing.price) || 0,
                      20,
                      6.5,
                      0.5,
                      150,
                      parseInt(currentListing.hoa) || 0
                    ).toLocaleString()}/mo
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    20% down, 6.5% rate, includes taxes + insurance + HOA
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-dark-border flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingListing(null);
                  setCurrentListing({ ...emptyListing });
                  setPhotoAnalysis(null);
                  setHighlightAiAnalysis(false);
                }}
                className="px-4 py-2 border border-dark-border rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!currentListing.address || !currentListing.price || saving}
                className="px-4 py-2 bg-primary text-dark font-medium rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                title={!currentListing.address ? 'Enter an address' : !currentListing.price ? 'Enter a price' : ''}
              >
                {saving && <AiOutlineLoading3Quarters className="animate-spin" />}
                {saving ? 'Saving...' : (editingListing !== null ? 'Update' : 'Add')} Listing
                {!currentListing.address || !currentListing.price ? (
                  <span className="text-xs opacity-70">
                    ({!currentListing.address ? 'need address' : 'need price'})
                  </span>
                ) : null}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-card rounded-xl border border-dark-border w-full max-w-md">
            <div className="p-6 border-b border-dark-border">
              <h2 className="text-xl font-semibold">Import Listings</h2>
            </div>

            <div className="p-6 space-y-6">
              {importStatus && (
                <div className={`p-3 rounded-lg text-sm ${
                  importStatus.includes('Error') ? 'bg-red-500/20 text-red-400' :
                  importStatus.includes('Syncing') ? 'bg-blue-500/20 text-blue-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {importStatus}
                </div>
              )}

              {/* Import from file */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">From File</h3>
                <p className="text-gray-500 text-sm mb-3">Import listings from a JSON file (exported from the extension or this app)</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".json"
                  onChange={handleImportFile}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dark-border rounded-lg text-gray-300 hover:text-white hover:border-primary/50 transition-colors"
                >
                  <AiOutlineUpload />
                  Choose JSON File
                </button>
              </div>

              {/* Sync from webhook server */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">From Webhook Server</h3>
                <p className="text-gray-500 text-sm mb-3">Sync listings from your email webhook server</p>
                <input
                  type="url"
                  placeholder="https://your-webhook-server.com"
                  defaultValue={localStorage.getItem('house-hunter-webhook-url') || ''}
                  onChange={(e) => localStorage.setItem('house-hunter-webhook-url', e.target.value)}
                  className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none mb-3"
                />
                <button
                  onClick={handleSyncFromServer}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-dark-border rounded-lg text-gray-300 hover:text-white hover:border-primary/50 transition-colors"
                >
                  <AiOutlineSync />
                  Sync from Server
                </button>
              </div>

              {/* Chrome Extension note */}
              <div className="bg-dark rounded-lg p-4 border border-dark-border">
                <h4 className="text-sm font-medium text-white mb-2">Chrome Extension</h4>
                <p className="text-gray-500 text-xs">
                  Use the House Hunter Chrome extension to capture listings directly from Zillow and Redfin.
                  Click "Export to App" in the extension popup, then import the JSON file here.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-dark-border">
              <button
                onClick={() => {
                  setShowImportModal(false);
                  setImportStatus('');
                }}
                className="w-full px-4 py-2 border border-dark-border rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-dark-card rounded-xl border border-dark-border w-full max-w-lg my-8">
            <div className="p-6 border-b border-dark-border">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <AiOutlineBell className="text-primary" />
                Alert Settings
              </h2>
              <p className="text-gray-400 text-sm mt-1">Configure automatic listing alerts</p>
            </div>

            <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Price Range */}
              <div>
                <label className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2 block">Price Range</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Min</label>
                    <input
                      type="number"
                      value={alertCriteria.minPrice}
                      onChange={(e) => setAlertCriteria({...alertCriteria, minPrice: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      step="10000"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Max</label>
                    <input
                      type="number"
                      value={alertCriteria.maxPrice}
                      onChange={(e) => setAlertCriteria({...alertCriteria, maxPrice: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      step="10000"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">${alertCriteria.minPrice.toLocaleString()} - ${alertCriteria.maxPrice.toLocaleString()}</p>
              </div>

              {/* Beds/Baths */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2 block">Min Beds</label>
                  <select
                    value={alertCriteria.minBeds}
                    onChange={(e) => setAlertCriteria({...alertCriteria, minBeds: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                  >
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}+</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2 block">Max Beds</label>
                  <select
                    value={alertCriteria.maxBeds}
                    onChange={(e) => setAlertCriteria({...alertCriteria, maxBeds: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                  >
                    {[3,4,5,6,7].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2 block">Min Baths</label>
                  <select
                    value={alertCriteria.minBaths}
                    onChange={(e) => setAlertCriteria({...alertCriteria, minBaths: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                  >
                    {[1,2,3,4].map(n => <option key={n} value={n}>{n}+</option>)}
                  </select>
                </div>
              </div>

              {/* Sqft */}
              <div>
                <label className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2 block">Square Footage</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Min</label>
                    <input
                      type="number"
                      value={alertCriteria.minSqft}
                      onChange={(e) => setAlertCriteria({...alertCriteria, minSqft: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      step="100"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Max</label>
                    <input
                      type="number"
                      value={alertCriteria.maxSqft}
                      onChange={(e) => setAlertCriteria({...alertCriteria, maxSqft: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      step="100"
                    />
                  </div>
                </div>
              </div>

              {/* Mountain View Filters */}
              <div className="bg-dark rounded-lg p-4 border border-dark-border">
                <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  🏔️ Mountain View Preferences
                </h3>

                <div className="space-y-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={alertCriteria.requireMountainView}
                      onChange={(e) => setAlertCriteria({...alertCriteria, requireMountainView: e.target.checked})}
                      className="w-4 h-4 rounded border-dark-border bg-dark text-primary focus:ring-primary"
                    />
                    <span className="text-gray-300">Only alert for potential mountain views</span>
                  </label>

                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">Preferred Orientations (back of house)</label>
                    <div className="flex flex-wrap gap-2">
                      {['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'].map(dir => (
                        <button
                          key={dir}
                          onClick={() => {
                            const current = alertCriteria.preferredOrientations || [];
                            const updated = current.includes(dir)
                              ? current.filter(d => d !== dir)
                              : [...current, dir];
                            setAlertCriteria({...alertCriteria, preferredOrientations: updated});
                          }}
                          className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                            alertCriteria.preferredOrientations?.includes(dir)
                              ? 'bg-primary/20 border-primary text-primary'
                              : 'bg-dark border-dark-border text-gray-400 hover:border-gray-500'
                          }`}
                        >
                          {dir}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">W, SW, NW face the mountains</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">Max Noise Level</label>
                    <select
                      value={alertCriteria.maxNoiseLevel}
                      onChange={(e) => setAlertCriteria({...alertCriteria, maxNoiseLevel: e.target.value})}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                    >
                      <option value="quiet">Quiet only</option>
                      <option value="moderate">Quiet or Moderate</option>
                      <option value="busy">Any</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 mb-2 block">Min Elevation (ft)</label>
                    <input
                      type="number"
                      value={alertCriteria.minElevation}
                      onChange={(e) => setAlertCriteria({...alertCriteria, minElevation: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-dark border border-dark-border rounded-lg focus:border-primary focus:outline-none"
                      step="100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Higher elevation = better view potential (6200+ recommended)</p>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="bg-dark rounded-lg p-4 border border-amber-500/30">
                <p className="text-amber-400 text-sm">
                  <strong>How it works:</strong> Every 2 hours, we check for new listings matching your criteria,
                  run AI satellite analysis on each one, and email you the ones with good mountain view potential.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-dark-border flex gap-3">
              <button
                onClick={testAlertCheck}
                disabled={runningTest}
                className="px-4 py-2 border border-dark-border rounded-lg text-gray-400 hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {runningTest && <AiOutlineLoading3Quarters className="animate-spin" />}
                {runningTest ? 'Running...' : 'Test Now'}
              </button>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 border border-dark-border rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveAlertCriteria}
                disabled={savingSettings}
                className="flex-1 px-4 py-2 bg-primary text-dark font-medium rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-slide-up">
          {toast.type === 'elevation' && (
            <div className="bg-dark-card border border-primary/50 rounded-xl p-4 shadow-lg shadow-primary/20 min-w-[280px]">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-2xl">🏔️</div>
                <div>
                  <div className="text-white font-semibold">Location Found!</div>
                  <div className="text-gray-400 text-sm">Elevation data loaded</div>
                </div>
              </div>
              <div className="bg-dark rounded-lg p-3 mb-3">
                <div className="text-center text-2xl tracking-widest font-mono mb-2">
                  {toast.mountain}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Elevation:</span>
                  <span className={`font-medium ${
                    toast.elevation === 'Ridge' ? 'text-green-400' :
                    toast.elevation === 'High' ? 'text-emerald-400' :
                    toast.elevation === 'Mid' ? 'text-yellow-400' : 'text-gray-400'
                  }`}>
                    {toast.elevation} ({toast.feet?.toLocaleString()} ft)
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-400">Faces:</span>
                  <span className="text-white font-medium">{toast.orientation} toward Pikes Peak</span>
                </div>
              </div>
              <div className="text-center text-sm">{toast.viewNote}</div>
            </div>
          )}

          {toast.type === 'import' && (
            <div className="bg-dark-card border border-green-500/50 rounded-xl p-4 shadow-lg shadow-green-500/20 min-w-[260px]">
              <div className="flex items-center gap-3">
                <div className="text-3xl">🏡</div>
                <div>
                  <div className="text-white font-semibold">
                    {toast.count} {toast.count === 1 ? 'House' : 'Houses'} Imported!
                  </div>
                  <div className="text-gray-400 text-sm">Ready for view assessment</div>
                </div>
              </div>
              <div className="mt-3 flex gap-1 justify-center">
                {Array.from({ length: Math.min(toast.count, 5) }).map((_, i) => (
                  <span key={i} className="text-xl animate-bounce" style={{ animationDelay: `${i * 100}ms` }}>🏠</span>
                ))}
                {toast.count > 5 && <span className="text-gray-400 self-center">+{toast.count - 5}</span>}
              </div>
            </div>
          )}

          {toast.type === 'error' && (
            <div className="bg-dark-card border border-red-500/50 rounded-xl p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="text-2xl">⚠️</div>
                <div className="text-white">{toast.message}</div>
              </div>
            </div>
          )}

          {toast.type === 'info' && (
            <div className="bg-dark-card border border-blue-500/50 rounded-xl p-4 shadow-lg shadow-blue-500/10 max-w-sm">
              <div className="flex items-start gap-3">
                <div className="text-2xl">📍</div>
                <div className="text-white text-sm">{toast.message}</div>
              </div>
            </div>
          )}

          {toast.type === 'analysis' && toast.analysis && (
            <div className="bg-dark-card border border-purple-500/50 rounded-xl p-4 shadow-lg shadow-purple-500/20 min-w-[300px] max-w-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-2xl">🤖</div>
                <div>
                  <div className="text-white font-semibold">AI Photo Analysis</div>
                  <div className="text-gray-400 text-xs">Confidence: {toast.analysis.confidence}</div>
                </div>
              </div>
              <div className="bg-dark rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Mountain View:</span>
                  <span className={toast.analysis.hasMountainView ? 'text-green-400' : 'text-gray-400'}>
                    {toast.analysis.hasMountainView ? 'Yes! 🏔️' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">View Quality:</span>
                  <span className={`font-medium ${
                    toast.analysis.viewQuality === 'excellent' ? 'text-green-400' :
                    toast.analysis.viewQuality === 'good' ? 'text-emerald-400' :
                    toast.analysis.viewQuality === 'partial' ? 'text-yellow-400' : 'text-gray-400'
                  }`}>
                    {toast.analysis.viewQuality}
                  </span>
                </div>
                {toast.analysis.facingDirection !== 'unknown' && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Likely Facing:</span>
                    <span className="text-white">{toast.analysis.facingDirection.replace('likely_', '').toUpperCase()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Photo Type:</span>
                  <span className="text-white">{toast.analysis.photoType?.replace('_', ' ')}</span>
                </div>
                {toast.analysis.deckOrPatio && (
                  <div className="text-purple-400 text-xs mt-2">Deck/patio visible</div>
                )}
              </div>
              {toast.analysis.viewDescription && (
                <p className="text-gray-400 text-xs mt-3 italic">"{toast.analysis.viewDescription}"</p>
              )}
              {toast.usage && (
                <p className="text-gray-600 text-xs mt-2">~{Math.round((toast.usage.inputTokens * 0.003 + toast.usage.outputTokens * 0.015) / 1000 * 100) / 100}¢</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Satellite Analysis Modal */}
      {showSatelliteModal && satelliteData && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-card rounded-2xl border border-cyan-500/50 shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-dark-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🛰️</span>
                  <h3 className="text-lg font-semibold text-white">AI Location Analysis</h3>
                </div>
                <button
                  onClick={() => setShowSatelliteModal(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <AiOutlineClose />
                </button>
              </div>
              {/* Elevation Summary Bar */}
              {satelliteData.elevationFeet && (
                <div className="flex items-center gap-3 text-sm bg-dark rounded-lg px-3 py-2">
                  <div className="flex items-center gap-1">
                    <span>⛰️</span>
                    <span className="text-gray-400">Elevation:</span>
                    <span className="text-white font-medium">{satelliteData.elevationFeet.toLocaleString()} ft</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      satelliteData.elevationCategory === 'Ridge' ? 'bg-green-500/20 text-green-400' :
                      satelliteData.elevationCategory === 'High' ? 'bg-emerald-500/20 text-emerald-400' :
                      satelliteData.elevationCategory === 'Mid' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {satelliteData.elevationCategory}
                    </span>
                  </div>
                  <div className="text-gray-500">|</div>
                  <div className="flex items-center gap-1">
                    <span>🧭</span>
                    <span className="text-gray-400">Mountains:</span>
                    <span className="text-white font-medium">{satelliteData.peakBearing}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 space-y-4">
              {/* Satellite Image with Compass and Annotation Overlays */}
              <div className="relative">
                <img
                  src={satelliteData.satelliteUrl}
                  alt="Satellite view"
                  className="w-full aspect-square object-cover rounded-lg border border-dark-border"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full aspect-square bg-dark rounded-lg border border-dark-border items-center justify-center text-gray-500 text-sm">
                  Satellite image unavailable
                </div>

                {/* AI Detection Annotations */}
                {satelliteData.analysis && (
                  <>
                    {/* Street Location Indicator */}
                    {satelliteData.analysis.streetLocation && satelliteData.analysis.streetLocation !== 'unknown' && (
                      <div className={`absolute bg-yellow-500/90 text-black text-[10px] font-bold px-2 py-0.5 rounded shadow-lg ${
                        satelliteData.analysis.streetLocation === 'top' ? 'top-1 left-1/2 -translate-x-1/2' :
                        satelliteData.analysis.streetLocation === 'bottom' ? 'bottom-1 left-1/2 -translate-x-1/2' :
                        satelliteData.analysis.streetLocation === 'left' ? 'left-1 top-1/2 -translate-y-1/2' :
                        satelliteData.analysis.streetLocation === 'right' ? 'right-1 top-1/2 -translate-y-1/2' :
                        satelliteData.analysis.streetLocation === 'top-left' ? 'top-1 left-1' :
                        satelliteData.analysis.streetLocation === 'top-right' ? 'top-1 right-1' :
                        satelliteData.analysis.streetLocation === 'bottom-left' ? 'bottom-1 left-1' :
                        'bottom-1 right-1'
                      }`}>
                        🛣️ STREET
                      </div>
                    )}

                    {/* Front/Back Direction Arrows */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      {/* Front arrow (pointing outward from center) */}
                      <div className={`absolute bg-blue-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-lg ${
                        satelliteData.analysis.frontFaces === 'N' ? 'top-8 left-1/2 -translate-x-1/2' :
                        satelliteData.analysis.frontFaces === 'S' ? 'bottom-8 left-1/2 -translate-x-1/2' :
                        satelliteData.analysis.frontFaces === 'W' ? 'left-8 top-1/2 -translate-y-1/2' :
                        satelliteData.analysis.frontFaces === 'E' ? 'right-8 top-1/2 -translate-y-1/2' :
                        satelliteData.analysis.frontFaces === 'NW' ? 'top-8 left-8' :
                        satelliteData.analysis.frontFaces === 'NE' ? 'top-8 right-8' :
                        satelliteData.analysis.frontFaces === 'SW' ? 'bottom-8 left-8' :
                        satelliteData.analysis.frontFaces === 'SE' ? 'bottom-8 right-8' :
                        'hidden'
                      }`}>
                        FRONT→{satelliteData.analysis.frontFaces}
                      </div>

                      {/* Back arrow (opposite side) */}
                      <div className={`absolute text-[10px] font-bold px-2 py-0.5 rounded shadow-lg ${
                        ['W', 'SW', 'NW'].includes(satelliteData.analysis.backFaces)
                          ? 'bg-green-500/90 text-white'
                          : 'bg-gray-500/90 text-white'
                      } ${
                        satelliteData.analysis.backFaces === 'S' ? 'bottom-8 left-1/2 -translate-x-1/2' :
                        satelliteData.analysis.backFaces === 'N' ? 'top-8 left-1/2 -translate-x-1/2' :
                        satelliteData.analysis.backFaces === 'E' ? 'right-8 top-1/2 -translate-y-1/2' :
                        satelliteData.analysis.backFaces === 'W' ? 'left-8 top-1/2 -translate-y-1/2' :
                        satelliteData.analysis.backFaces === 'SE' ? 'bottom-8 right-8' :
                        satelliteData.analysis.backFaces === 'SW' ? 'bottom-8 left-8' :
                        satelliteData.analysis.backFaces === 'NE' ? 'top-8 right-8' :
                        satelliteData.analysis.backFaces === 'NW' ? 'top-8 left-8' :
                        'hidden'
                      }`}>
                        BACK→{satelliteData.analysis.backFaces} {['W', 'SW', 'NW'].includes(satelliteData.analysis.backFaces) ? '🏔️' : ''}
                      </div>
                    </div>
                  </>
                )}

                {/* Compass Rose Overlay */}
                <div className="absolute top-2 right-2 w-16 h-16 bg-dark/90 rounded-full border border-cyan-500/30 flex items-center justify-center shadow-lg">
                  <div className="relative w-12 h-12">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-[10px] text-white font-bold absolute top-0 left-1/2 -translate-x-1/2">N</div>
                      <div className="text-[10px] text-gray-400 absolute bottom-0 left-1/2 -translate-x-1/2">S</div>
                      <div className="text-[10px] text-cyan-400 font-bold absolute left-0 top-1/2 -translate-y-1/2">W</div>
                      <div className="text-[10px] text-gray-400 absolute right-0 top-1/2 -translate-y-1/2">E</div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-1 h-4 bg-red-500 rounded-full -translate-y-1"></div>
                    </div>
                  </div>
                </div>
                {/* Arrow to Pikes Peak */}
                <div className="absolute bottom-2 left-2 bg-dark/90 rounded-lg px-3 py-1.5 border border-cyan-500/30 flex items-center gap-2 text-sm shadow-lg">
                  <span>🏔️</span>
                  <span className="text-white">Mountains: <span className="text-cyan-400 font-medium">{satelliteData.peakBearing}</span></span>
                </div>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-2 text-[10px]">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                  <span className="text-gray-400">Street</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span className="text-gray-400">Front</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-gray-400">Back (good view)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-gray-500 rounded"></div>
                  <span className="text-gray-400">Back (no view)</span>
                </div>
              </div>

              {/* Analysis Results */}
              {satelliteData.analysis && (
                <div className="bg-dark rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">🤖</span>
                    <span className="text-white font-medium">AI Analysis</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      satelliteData.analysis.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                      satelliteData.analysis.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {satelliteData.analysis.confidence} confidence
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-dark-card rounded-lg p-3 border border-dark-border">
                      <div className="text-gray-400 text-xs mb-1">Front Faces</div>
                      <div className="text-white font-medium text-lg">{satelliteData.analysis.frontFaces}</div>
                    </div>
                    <div className="bg-dark-card rounded-lg p-3 border border-dark-border">
                      <div className="text-gray-400 text-xs mb-1">Back Faces</div>
                      <div className={`font-medium text-lg ${
                        ['W', 'SW', 'NW'].includes(satelliteData.analysis.backFaces) ? 'text-green-400' : 'text-white'
                      }`}>
                        {satelliteData.analysis.backFaces}
                        {['W', 'SW', 'NW'].includes(satelliteData.analysis.backFaces) && ' 🎯'}
                      </div>
                    </div>
                  </div>

                  {satelliteData.analysis.deckLocation !== 'none_visible' && (
                    <div className="bg-dark-card rounded-lg p-3 border border-dark-border">
                      <div className="text-gray-400 text-xs mb-1">Deck/Patio</div>
                      <div className="text-white">
                        Located on <span className="text-cyan-400">{satelliteData.analysis.deckLocation.replace('_', ' ')}</span>
                        {satelliteData.analysis.deckFaces !== 'unknown' && (
                          <span>, faces <span className={
                            ['W', 'SW', 'NW'].includes(satelliteData.analysis.deckFaces) ? 'text-green-400 font-medium' : ''
                          }>{satelliteData.analysis.deckFaces}</span></span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-dark-card rounded-lg p-3 border border-dark-border">
                    <div className="text-gray-400 text-xs mb-1">Mountain View Potential</div>
                    <div className={`font-medium text-lg ${
                      satelliteData.analysis.mountainViewPotential === 'excellent' ? 'text-green-400' :
                      satelliteData.analysis.mountainViewPotential === 'likely' ? 'text-emerald-400' :
                      satelliteData.analysis.mountainViewPotential === 'possible' ? 'text-yellow-400' :
                      'text-gray-400'
                    }`}>
                      {satelliteData.analysis.mountainViewPotential.charAt(0).toUpperCase() + satelliteData.analysis.mountainViewPotential.slice(1)}
                      {satelliteData.analysis.mountainViewPotential === 'excellent' && ' 🏔️✨'}
                      {satelliteData.analysis.mountainViewPotential === 'likely' && ' 🏔️'}
                    </div>
                  </div>

                  {satelliteData.analysis.noiseLevel && (
                    <div className="bg-dark-card rounded-lg p-3 border border-dark-border">
                      <div className="text-gray-400 text-xs mb-1">Noise Level</div>
                      <div className={`font-medium text-lg ${
                        satelliteData.analysis.noiseLevel === 'quiet' ? 'text-green-400' :
                        satelliteData.analysis.noiseLevel === 'moderate' ? 'text-yellow-400' :
                        satelliteData.analysis.noiseLevel === 'busy' ? 'text-orange-400' :
                        'text-red-400'
                      }`}>
                        {satelliteData.analysis.noiseLevel === 'quiet' && '🤫 '}
                        {satelliteData.analysis.noiseLevel === 'moderate' && '🔉 '}
                        {satelliteData.analysis.noiseLevel === 'busy' && '🔊 '}
                        {satelliteData.analysis.noiseLevel === 'loud' && '📢 '}
                        {satelliteData.analysis.noiseLevel.charAt(0).toUpperCase() + satelliteData.analysis.noiseLevel.slice(1)}
                      </div>
                      {satelliteData.analysis.noiseFactors && satelliteData.analysis.noiseFactors.length > 0 && (
                        <div className="text-gray-500 text-xs mt-1">
                          {satelliteData.analysis.noiseFactors.map(f => f.replace(/_/g, ' ')).join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {satelliteData.analysis.reasoning && (
                    <p className="text-gray-400 text-xs italic">"{satelliteData.analysis.reasoning}"</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSatelliteModal(false)}
                  className="flex-1 px-4 py-2 border border-dark-border rounded-lg text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => applySatelliteAnalysis(satelliteData.analysis)}
                  className="flex-1 px-4 py-2 bg-cyan-500 text-dark font-medium rounded-lg hover:bg-cyan-400 transition-colors"
                >
                  ✓ Apply Analysis
                </button>
              </div>

              {satelliteData.usage && (
                <p className="text-gray-600 text-xs text-center">
                  ~{Math.round((satelliteData.usage.inputTokens * 0.003 + satelliteData.usage.outputTokens * 0.015) / 1000 * 100) / 100}¢
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Guided Tour */}
      {showTour && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/70 z-40" onClick={skipTour} />

          {/* Highlight current element */}
          {tourSteps[tourStep].target !== 'welcome' && tourSteps[tourStep].target !== 'done' && (
            <style>{`
              [data-tour="${tourSteps[tourStep].target}"] {
                position: relative;
                z-index: 50;
                box-shadow: 0 0 0 4px rgba(20, 240, 110, 0.5), 0 0 20px rgba(20, 240, 110, 0.3);
                border-radius: 8px;
              }
            `}</style>
          )}

          {/* Tour tooltip */}
          <div className={`fixed z-50 ${
            tourSteps[tourStep].position === 'center'
              ? 'inset-0 flex items-center justify-center p-4'
              : 'top-32 left-1/2 -translate-x-1/2'
          }`}>
            <div className={`bg-dark-card rounded-2xl border border-primary/50 shadow-xl shadow-primary/20 ${
              tourSteps[tourStep].position === 'center' ? 'w-full max-w-md' : 'w-80'
            }`}>
              {/* Progress dots */}
              <div className="flex justify-center gap-1.5 pt-4">
                {tourSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === tourStep ? 'bg-primary' : i < tourStep ? 'bg-primary/50' : 'bg-dark-border'
                    }`}
                  />
                ))}
              </div>

              <div className="p-5">
                <h3 className="text-lg font-semibold text-white mb-2">
                  {tourSteps[tourStep].title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {tourSteps[tourStep].content}
                </p>
              </div>

              <div className="flex items-center justify-between p-4 pt-0 gap-3">
                <button
                  onClick={skipTour}
                  className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                >
                  Skip tour
                </button>
                <div className="flex gap-2">
                  {tourStep > 0 && (
                    <button
                      onClick={prevTourStep}
                      className="px-4 py-2 text-sm border border-dark-border rounded-lg text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={nextTourStep}
                    className="px-4 py-2 text-sm bg-primary text-dark font-medium rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    {tourStep === tourSteps.length - 1 ? 'Get Started' : 'Next'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Neighborhood Info Modal */}
      {showNeighborhoodModal && neighborhoodContent && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-card rounded-xl border border-dark-border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-border">
              <h2 className="text-xl font-semibold text-white">{neighborhoodContent.name}</h2>
              <button
                onClick={() => setShowNeighborhoodModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <AiOutlineClose size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-dark rounded-lg p-4 border border-dark-border">
                  <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Elevation</div>
                  <div className="text-white font-medium">{neighborhoodContent.elevation}</div>
                </div>
                <div className="bg-dark rounded-lg p-4 border border-dark-border">
                  <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">View Quality</div>
                  <div className="text-green-400 font-medium">{neighborhoodContent.viewQuality}</div>
                </div>
              </div>

              {/* Schools */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Schools</h3>
                <div className="bg-dark rounded-lg p-4 border border-dark-border space-y-2">
                  <div className="text-primary text-sm font-medium">{neighborhoodContent.schools.district}</div>
                  <div className="text-gray-300 text-sm">Elementary: {neighborhoodContent.schools.elementary}</div>
                  <div className="text-gray-300 text-sm">Middle: {neighborhoodContent.schools.middle}</div>
                  <div className="text-gray-300 text-sm">High: {neighborhoodContent.schools.high}</div>
                </div>
              </div>

              {/* Safety */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Safety</h3>
                <p className="text-gray-300 text-sm">{neighborhoodContent.safety}</p>
              </div>

              {/* HOA */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">HOAs</h3>
                <p className="text-gray-300 text-sm">{neighborhoodContent.hoa}</p>
              </div>

              {/* Lifestyle */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Lifestyle</h3>
                <p className="text-gray-300 text-sm">{neighborhoodContent.lifestyle}</p>
              </div>

              {/* Real Estate */}
              <div>
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3">Real Estate</h3>
                <p className="text-gray-300 text-sm">{neighborhoodContent.realEstate}</p>
              </div>

              {/* Pros & Cons */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-green-400 uppercase tracking-wider mb-3">Pros</h3>
                  <ul className="space-y-1">
                    {neighborhoodContent.pros.map((pro, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                        <span className="text-green-400">+</span> {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-red-400 uppercase tracking-wider mb-3">Cons</h3>
                  <ul className="space-y-1">
                    {neighborhoodContent.cons.map((con, i) => (
                      <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                        <span className="text-red-400">-</span> {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Best For */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <h3 className="text-sm font-medium text-primary uppercase tracking-wider mb-2">Best For</h3>
                <p className="text-gray-300 text-sm">{neighborhoodContent.bestFor}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Results Modal */}
      {showTestResultsModal && testResults && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-card rounded-xl border border-dark-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-dark-border">
              <div>
                <h2 className="text-xl font-semibold text-white">Listing Check Results</h2>
                <p className="text-gray-400 text-sm mt-1">{testResults.message}</p>
              </div>
              <button
                onClick={() => setShowTestResultsModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <AiOutlineClose size={20} />
              </button>
            </div>

            {/* Criteria Summary */}
            {testResults.criteria && (
              <div className="px-6 py-3 bg-dark border-b border-dark-border">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Search Criteria</div>
                <div className="text-sm text-gray-300">
                  ${testResults.criteria.minPrice?.toLocaleString() || 0} - ${testResults.criteria.maxPrice?.toLocaleString()} | {testResults.criteria.minBeds || 0}-{testResults.criteria.maxBeds} beds | {testResults.criteria.minBaths}+ baths | {testResults.criteria.minSqft?.toLocaleString() || 0}-{testResults.criteria.maxSqft?.toLocaleString()} sqft
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {testResults.allListings && testResults.allListings.length > 0 ? (
                <div className="space-y-4">
                  {/* Recommended section */}
                  {testResults.allListings.filter(l => l.meetsViewCriteria).length > 0 && (
                    <div>
                      <h3 className="text-primary font-medium mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-primary rounded-full"></span>
                        Recommended ({testResults.allListings.filter(l => l.meetsViewCriteria).length})
                      </h3>
                      <div className="space-y-3">
                        {testResults.allListings.filter(l => l.meetsViewCriteria).map((listing, i) => (
                          <div key={i} className="bg-primary/10 border border-primary/30 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="font-medium text-white">{listing.address}</div>
                                <div className="text-sm text-gray-400 mt-1">
                                  ${parseInt(listing.price || 0).toLocaleString()} | {listing.beds} bed | {listing.baths} bath | {parseInt(listing.sqft || 0).toLocaleString()} sqft
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  View: {listing.viewPotential} | Back faces: {listing.backFaces} | Elevation: {listing.elevation ? `${listing.elevation.toLocaleString()} ft` : '?'} | Noise: {listing.noiseLevel}
                                </div>
                              </div>
                              {listing.link && (
                                <a
                                  href={listing.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline text-sm whitespace-nowrap"
                                >
                                  View →
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Others section */}
                  {testResults.allListings.filter(l => !l.meetsViewCriteria).length > 0 && (
                    <div>
                      <h3 className="text-gray-400 font-medium mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                        Other Listings ({testResults.allListings.filter(l => !l.meetsViewCriteria).length})
                      </h3>
                      <div className="space-y-3">
                        {testResults.allListings.filter(l => !l.meetsViewCriteria).map((listing, i) => (
                          <div key={i} className="bg-dark border border-dark-border rounded-lg p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="font-medium text-white">{listing.address}</div>
                                <div className="text-sm text-gray-400 mt-1">
                                  ${parseInt(listing.price || 0).toLocaleString()} | {listing.beds} bed | {listing.baths} bath | {parseInt(listing.sqft || 0).toLocaleString()} sqft
                                </div>
                                <div className="text-sm text-gray-500 mt-1">
                                  View: {listing.viewPotential} | Back faces: {listing.backFaces} | Elevation: {listing.elevation ? `${listing.elevation.toLocaleString()} ft` : '?'} | Noise: {listing.noiseLevel}
                                  {listing.error && <span className="text-red-400 ml-2">({listing.error})</span>}
                                </div>
                              </div>
                              {listing.link && (
                                <a
                                  href={listing.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-400 hover:text-primary hover:underline text-sm whitespace-nowrap"
                                >
                                  View →
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <p>No new listings found.</p>
                  <p className="text-sm mt-2">All listings from the search are already in your tracker.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-dark-border bg-dark">
              <div className="text-sm text-gray-500">
                {testResults.emailSent ? 'Email sent with results' : 'Email not configured'}
              </div>
              <button
                onClick={() => setShowTestResultsModal(false)}
                className="px-4 py-2 bg-primary text-dark font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help button to restart tour */}
      {!showTour && (
        <button
          onClick={restartTour}
          className="fixed bottom-6 left-6 z-30 p-3 bg-dark-card border border-dark-border rounded-full text-gray-400 hover:text-primary hover:border-primary/50 transition-colors shadow-lg"
          title="Show tour again"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </button>
      )}

      {/* Footer with demo notice and portfolio link */}
      <div className="fixed bottom-6 right-6 z-30 flex flex-col items-end gap-2">
        <div className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400/80 text-[10px] flex items-center gap-1.5">
          <span>Demo Mode</span>
          <span className="text-amber-500/50">·</span>
          <span className="text-amber-400/60">Data stored locally</span>
        </div>
        <a
          href="https://hereshecodes.com"
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-2 bg-dark-card border border-dark-border rounded-full text-gray-500 hover:text-primary hover:border-primary/50 transition-colors shadow-lg text-xs flex items-center gap-1.5"
          title="View portfolio"
        >
          <span>Built by</span>
          <span className="text-gray-300 hover:text-primary">hereshecodes</span>
        </a>
      </div>
    </div>
  );
}

export default App;
